import { useState, useEffect, useCallback } from "react";
import Auth from "./Auth";
import { supabase } from "./supabaseclient";
import { printPatientReport } from "./PatientReport";
import OutcomeMeasures from "./OutcomeMeasures";
import BillingTab from "./BillingTab";
import ProgressAnalytics from "./ProgressAnalytics";
import KnowledgeHub from "./KnowledgeHub";
import Analytics from "./Analytics";
import SOAPNoteAI from "./SOAPNoteAI";
import ResearchModule from "./ResearchModule";
import { useLocalDraft } from "./useLocalDraft";
import { usePlan } from "./usePlan";
import { useOrganization } from "./useOrganization";
import UpgradeWall from "./UpgradeWall";
import TeamManagement from "./TeamManagement";
import ResetPassword from "./ResetPassword";
import WhatsAppReminderButton from './components/WhatsAppReminderButton';

const TODAY = new Date().toISOString().split("T")[0];

const CONDITIONS = [
  "Right Lateral Epicondylitis","Left Lateral Epicondylitis",
  "Right SLAP Lesion","Left SLAP Lesion",
  "Right Periarthritis Shoulder","Left Periarthritis Shoulder",
  "Lumbar Disc Herniation","Frozen Shoulder","Knee Osteoarthritis",
  "ACL Tear","Cervical Spondylosis","Rotator Cuff Injury",
  "Plantar Fasciitis","Tennis Elbow","Post-surgical Rehab",
  "Stroke Rehabilitation","Other",
];

const BLOOD_GROUPS = ["—","A+","A−","B+","B−","AB+","AB−","O+","O−"];

const EMPTY_PATIENT = {
  patientId:"",name:"",age:"",gender:"Male",blood:"—",
  phone:"",email:"",address:"",condition:"",customCondition:"",
  jointSelection:"",painScore:5,therapist:"",sessions:0,
  totalSessions:12,nextVisit:"",status:"Active",joinDate:TODAY,
  diagnosticTools:"",outcomeMeasures:"",specialTests:"",notes:"",
  bp:"",pulse:"",weight:"",payments:"Pending",amount:0,
  pastHistory:"",medicalHistory:"",surgicalHistory:"",history:[],
};

const EMPTY_THERAPIST = { name:"", specialty:"", avatar:"" };

function getInitials(name="") {
  const clean = name.replace(/^(Dr|Mr|Ms|Mrs)\.?\s*/i,"");
  const parts = clean.trim().split(/\s+/);
  return parts.slice(0,2).map((w)=>w[0]||"").join("").toUpperCase()||"?";
}

function autoAvatar(name="") { return getInitials(name); }

function dbToPatient(r) {
  return {
    id:r.id, name:r.name,
    patientId:r.custom_patient_id||"",
    age:r.age, gender:r.gender, blood:r.blood,
    phone:r.phone, email:r.email, address:r.address,
    condition:r.condition,
    customCondition:r.custom_condition||"",
    jointSelection:r.joint_selection||"",
    painScore:r.pain_score??5,
    therapist:r.therapist, sessions:r.sessions,
    totalSessions:r.total_sessions,
    nextVisit:r.next_visit||"",
    status:r.status, joinDate:r.join_date||TODAY,
    diagnosticTools:r.diagnostic_tools||"",
    outcomeMeasures:r.outcome_measures||"",
    specialTests:r.special_tests||"",
    notes:r.notes,
    vitals:{bp:r.bp,pulse:r.pulse,weight:r.weight},
    payments:r.payments, amount:Number(r.amount)||0,
    pastHistory:r.past_history||"",
    medicalHistory:r.medical_history||"",
    surgicalHistory:r.surgical_history||"",
    history:r.history||[],
  };
}

function patientToDb(f) {
  return {
    name:f.name, custom_patient_id:f.patientId||null,
    age:Number(f.age)||null, gender:f.gender, blood:f.blood,
    phone:f.phone, email:f.email, address:f.address,
    condition:f.condition, custom_condition:f.customCondition||"",
    joint_selection:f.jointSelection||"",
    pain_score:Number(f.painScore),
    therapist:f.therapist, sessions:f.sessions||0,
    total_sessions:f.totalSessions,
    next_visit:f.nextVisit||null, status:f.status,
    join_date:f.joinDate,
    diagnostic_tools:f.diagnosticTools||"",
    outcome_measures:f.outcomeMeasures||"",
    special_tests:f.specialTests||"",
    notes:f.notes, bp:f.bp||"", pulse:f.pulse||"",
    weight:f.weight||"", payments:f.payments,
    amount:Number(f.amount)||0,
    past_history:f.pastHistory||"",
    medical_history:f.medicalHistory||"",
    surgical_history:f.surgicalHistory||"",
    history:f.history||[],
  };
}

function getProgress(p) {
  return Math.min(Math.round((p.sessions/Math.max(p.totalSessions,1))*100),100);
}

function getAvatarColor(name="") {
  const palette=["#1B4F72","#145A32","#6E2F0E","#512E5F","#6B2737","#0E6655","#154360","#784212","#1A5276","#186A3B"];
  const hash=[...name].reduce((a,c)=>a+c.charCodeAt(0),0);
  return palette[hash%palette.length];
}

function getPainLabel(score) {
  if(score>=7)return"Severe";
  if(score>=4)return"Moderate";
  return"Mild";
}

function getPainColor(score) {
  if(score>=7)return"#f87171";
  if(score>=4)return"#fbbf24";
  return"#34d399";
}

function getDisplayCondition(p) {
  if(p.condition==="Other"&&p.customCondition)return p.customCondition;
  return p.condition;
}

function inferJointFromCondition(condition,customCondition="") {
  const value=(condition==="Other"?customCondition:condition||"").toLowerCase();
  if(value.includes("shoulder")||value.includes("rotator cuff")||value.includes("slap")||value.includes("periarthritis")||value.includes("adhesive capsulitis")||value.includes("frozen shoulder"))return"shoulder";
  if(value.includes("elbow")||value.includes("lateral epicondylitis")||value.includes("medial epicondylitis")||value.includes("tennis elbow"))return"elbow";
  if(value.includes("de quervain")||value.includes("carpal tunnel")||value.includes("wrist")||value.includes("hand")||value.includes("thumb"))return"wrist_hand";
  if(value.includes("cervical")||value.includes("neck"))return"cervical_spine";
  if(value.includes("thoracic"))return"thoracic_spine";
  if(value.includes("lumbar")||value.includes("low back")||value.includes("disc")||value.includes("sciatica")||value.includes("radiculopathy"))return"lumbar_spine";
  if(value.includes("hip")||value.includes("piriformis"))return"hip";
  if(value.includes("knee")||value.includes("acl")||value.includes("patello")||value.includes("osteoarthritis"))return"knee";
  if(value.includes("ankle")||value.includes("foot")||value.includes("heel")||value.includes("plantar fasciitis")||value.includes("achilles"))return"ankle_foot";
  if(value.includes("stroke")||value.includes("neuro"))return"neuro";
  return"";
}

function getEffectiveJoint(patient) {
  if(!patient)return"";
  if(patient.jointSelection)return patient.jointSelection;
  return inferJointFromCondition(patient.condition,patient.customCondition);
}

function getGreeting() {
  const h=new Date().getHours();
  if(h<12)return"Good Morning";
  if(h<17)return"Good Afternoon";
  return"Good Evening";
}

const T = {
  bg:"#06090f",panel:"#0b0f1a",card:"#0f1623",border:"#1a2540",
  gold:"#c9a96e",goldLight:"#e8c98a",goldDim:"#7a6640",
  text:"#e8eaf0",textMid:"#7a8599",textDim:"#3d4a5c",
  active:"#10b981",activeBg:"#071f14",activeText:"#34d399",
  completedBg:"#13132e",completedText:"#a5b4fc",moderate:"#fbbf24",
};

function Avatar({name,size=38}) {
  return(
    <div style={{width:size,height:size,borderRadius:"50%",background:getAvatarColor(name),display:"flex",alignItems:"center",justifyContent:"center",fontSize:size*0.33,fontWeight:700,color:"#fff",flexShrink:0}}>
      {getInitials(name)}
    </div>
  );
}

function ProgressBar({pct,height=5}) {
  return(
    <div style={{height,borderRadius:3,background:T.border,overflow:"hidden"}}>
      <div style={{height:"100%",borderRadius:3,width:`${pct}%`,background:`linear-gradient(90deg,${T.gold},${T.goldLight})`,transition:"width 0.5s ease"}}/>
    </div>
  );
}

function StatusBadge({status}) {
  const a=status==="Active";
  return<span style={{display:"inline-flex",alignItems:"center",fontSize:10,fontWeight:700,letterSpacing:"0.6px",textTransform:"uppercase",padding:"3px 10px",borderRadius:20,background:a?T.activeBg:T.completedBg,color:a?T.activeText:T.completedText}}>{status}</span>;
}

function PaymentBadge({payments}) {
  const p=payments==="Paid";
  return<span style={{display:"inline-flex",alignItems:"center",fontSize:10,fontWeight:700,letterSpacing:"0.6px",textTransform:"uppercase",padding:"3px 10px",borderRadius:20,background:p?T.activeBg:"rgba(251,191,36,0.1)",color:p?T.activeText:T.moderate}}>{payments}</span>;
}

function SectionLabel({text}) {
  return<div style={{fontSize:10,fontWeight:700,letterSpacing:"0.9px",textTransform:"uppercase",color:T.goldDim,marginBottom:13}}>{text}</div>;
}

function Divider({my=18}) {
  return<div style={{height:1,background:T.border,marginTop:my,marginBottom:my}}/>;
}

function FieldLabel({children}) {
  return<label style={{display:"block",fontSize:11,fontWeight:600,letterSpacing:"0.7px",textTransform:"uppercase",color:T.textDim,marginBottom:5}}>{children}</label>;
}

const GLOBAL_CSS=`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  *{box-sizing:border-box;margin:0;padding:0;}
  body{background:${T.bg};font-family:'DM Sans',sans-serif;}
  ::-webkit-scrollbar{width:4px;height:4px;}
  ::-webkit-scrollbar-track{background:${T.panel};}
  ::-webkit-scrollbar-thumb{background:${T.border};border-radius:2px;}
  input,select,textarea{background:${T.panel}!important;color:${T.text}!important;border:1px solid ${T.border}!important;border-radius:7px!important;padding:10px 14px!important;font-family:'DM Sans',sans-serif!important;font-size:13px!important;outline:none!important;width:100%!important;transition:border-color 0.2s,box-shadow 0.2s!important;}
  input:focus,select:focus,textarea:focus{border-color:${T.gold}!important;box-shadow:0 0 0 3px rgba(201,169,110,0.08)!important;}
  select option{background:${T.panel};}
  .nav-link{cursor:pointer;padding:9px 13px;border-radius:7px;font-size:13px;font-weight:500;color:${T.textMid};transition:all 0.15s;display:flex;align-items:center;gap:9px;border-left:2px solid transparent;margin-bottom:2px;}
  .nav-link:hover{background:${T.card};color:${T.text};}
  .nav-link.active{background:linear-gradient(135deg,rgba(201,169,110,0.12),rgba(201,169,110,0.04));color:${T.gold};border-left-color:${T.gold};}
  .card{background:${T.card};border:1px solid ${T.border};border-radius:10px;}
  .row-hover{transition:background 0.12s;}
  .row-hover:hover{background:rgba(255,255,255,0.025)!important;}
  .tab{cursor:pointer;padding:9px 18px;font-size:11px;font-weight:700;letter-spacing:0.8px;text-transform:uppercase;color:${T.textMid};border-bottom:2px solid transparent;transition:all 0.18s;}
  .tab:hover{color:${T.text};}
  .tab.active{color:${T.gold};border-bottom-color:${T.gold};}
  .btn{cursor:pointer;border:none;font-family:'DM Sans',sans-serif;font-weight:500;transition:all 0.18s;border-radius:7px;}
  .btn-gold{background:linear-gradient(135deg,${T.gold},${T.goldLight});color:#07090e;padding:10px 22px;font-size:13px;font-weight:600;}
  .btn-gold:hover{opacity:0.88;transform:translateY(-1px);box-shadow:0 6px 22px rgba(201,169,110,0.28);}
  .btn-gold:disabled{opacity:0.5;cursor:not-allowed;transform:none;}
  .btn-outline{background:transparent;border:1px solid ${T.border}!important;color:${T.textMid};padding:8px 18px;font-size:13px;}
  .btn-outline:hover{border-color:${T.gold}!important;color:${T.gold};}
  .btn-danger{background:rgba(248,113,113,0.08);border:1px solid rgba(248,113,113,0.25)!important;color:#f87171;padding:8px 18px;font-size:13px;}
  .btn-danger:hover{background:rgba(248,113,113,0.18);}
  .btn-sm{padding:6px 13px!important;font-size:12px!important;}
  .btn-session{background:rgba(201,169,110,0.1);border:1px solid ${T.goldDim};color:${T.gold};border-radius:7px;cursor:pointer;padding:6px 10px;font-size:12px;font-weight:600;font-family:'DM Sans',sans-serif;transition:all 0.18s;}
  .btn-session:hover{background:rgba(201,169,110,0.2);}
  .modal-backdrop{position:fixed;inset:0;background:rgba(0,0,0,0.75);backdrop-filter:blur(6px);z-index:100;display:flex;align-items:center;justify-content:center;padding:16px;}
  .stat-card{background:${T.card};border:1px solid ${T.border};border-radius:10px;padding:20px 22px;position:relative;overflow:hidden;}
  .stat-card::after{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:var(--accent-color);}
  @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  @keyframes spin{to{transform:rotate(360deg)}}
  .fade-up{animation:fadeUp 0.28s ease;}
`;

export default function App() {
  const [session,setSession]=useState(null);
  const [planLoading,setPlanLoading]=useState(false);
  const [sidebarOpen,setSidebarOpen]=useState(false);
  const [isMobile,setIsMobile]=useState(false);
  const [patients,setPatients]=useState([]);
  const [therapists,setTherapists]=useState([]);
  const [ready,setReady]=useState(false);
  const [dbError,setDbError]=useState(null);
  const [page,setPage]=useState("dashboard");
  const [selected,setSelected]=useState(null);
  const [detailTab,setDetailTab]=useState("overview");
  const [showPatientForm,setShowPatientForm]=useState(false);
  const [editPatientId,setEditPatientId]=useState(null);
  const [patientForm,setPatientForm]=useState({...EMPTY_PATIENT});
  const [savingPatient,setSavingPatient]=useState(false);
  const [savingDiagnostics,setSavingDiagnostics]=useState(false);
  const [attachmentFile,setAttachmentFile]=useState(null);
  const [uploadingAttachment,setUploadingAttachment]=useState(false);
  const [patientAttachments,setPatientAttachments]=useState([]);
  const [homeProgramme,setHomeProgramme]=useState({
    programme_title:"",condition:"",goal:"",
    exercise_list:"",precautions:"",patient_advice:"",video_link:"",
  });
  const [savingHomeProgramme,setSavingHomeProgramme]=useState(false);
  const [editingHomeProgramme,setEditingHomeProgramme]=useState(false);
  const [loadingAttachments,setLoadingAttachments]=useState(false);
  const [showTherapistForm,setShowTherapistForm]=useState(false);
  const [editTherapistId,setEditTherapistId]=useState(null);
  const [therapistForm,setTherapistForm]=useState({...EMPTY_THERAPIST});
  const [savingTherapist,setSavingTherapist]=useState(false);
  const [clinicSettings,setClinicSettings]=useState({
    clinic_name:"",owner_name:"",phone:"",email:"",
    address:"",signature_name:"",designation:"",signature_image_url:"",
  });
  const [savingClinicSettings,setSavingClinicSettings]=useState(false);
  const [signatureFile,setSignatureFile]=useState(null);
  const [uploadingSignature,setUploadingSignature]=useState(false);
  const [newPassword,setNewPassword]=useState("");
  const [confirmNewPassword,setConfirmNewPassword]=useState("");
  const [changingPassword,setChangingPassword]=useState(false);
  const [passwordMessage,setPasswordMessage]=useState("");
  const [editingClinicSettings,setEditingClinicSettings]=useState(false);
  const [search,setSearch]=useState("");
  const [filterStatus,setFilterStatus]=useState("All");
  const [filterTherapist,setFilterTherapist]=useState("All");
  const [sortBy,setSortBy]=useState("name");
  const [patientDraft,setPatientDraft,clearPatientDraft]=useLocalDraft("patient_form_draft",{...EMPTY_PATIENT});

  // ✅ FIXED - Added basePlanName to destructure
  const { currentOrg, organizations, switchOrganization, teamMembers, inviteTeamMember, removeTeamMember, loading: orgLoading } = useOrganization(session);
  const {
    hasFeature,
    planName,
    basePlanName,
    trialDaysLeft,
    isTrialExpired,
    isTrialActive,
    subscriptionRequired,
    isAdmin,
    therapistLimit,
    patientLimit,

  }=usePlan(session, currentOrg?.id);

  const [upgradeWallFeature,setUpgradeWallFeature]=useState(null);
  const [toast,setToast]=useState(null);
  const [showUpgrade,setShowUpgrade]=useState(false);
  const [historyInput,setHistoryInput]=useState("");
  const [confirmDeleteId,setConfirmDeleteId]=useState(null);
  const [confirmDeleteTherapistId,setConfirmDeleteTherapistId]=useState(null);

  useEffect(()=>{
    function check(){setIsMobile(window.innerWidth<=768);}
    check();
    window.addEventListener("resize",check);
    window.addEventListener("orientationchange",()=>{setTimeout(check,100);});
    return()=>{window.removeEventListener("resize",check);};
  },[]);

  function notify(msg,type="success"){
    setToast({msg,type});
    setTimeout(()=>setToast(null),3500);
  }

  // ✅ FIXED - openUpgrade now uses UpgradeWall properly
  function openUpgrade(reason="",featureKey=null){
    setUpgradeWallFeature(featureKey);
    setShowUpgrade(true);
  }

  const fetchPatients=useCallback(async()=>{
    if(!session?.user?.id)return;
    const{data,error}=await supabase.from("patients").select("*").eq("user_id",session.user.id).order("created_at",{ascending:false});
    if(error){setDbError(error.message);return;}
    setPatients((data||[]).map(dbToPatient));
  },[session]);

  const fetchTherapists=useCallback(async()=>{
    if(!session?.user?.id)return;
    const{data,error}=await supabase.from("therapists").select("*").eq("user_id",session.user.id).order("created_at",{ascending:true});
    if(error){setDbError(error.message);return;}
    setTherapists(data||[]);
  },[session]);

  const fetchClinicSettings=useCallback(async()=>{
    if(!session?.user?.id)return;
    const{data,error}=await supabase.from("clinic_settings").select("*").eq("user_id",session.user.id).maybeSingle();
    if(error){console.log("Clinic settings fetch error:",error.message);return;}
    if(data){setClinicSettings({
      clinic_name:data.clinic_name||"",
      owner_name:data.owner_name||"",
      phone:data.phone||"",
      email:data.email||"",
      address:data.address||"",
      signature_name:data.signature_name||"",
      designation:data.designation||"",
      signature_image_url:data.signature_image_url||"",
    });}
  },[session]);

  async function saveClinicSettings(){
    if(!session?.user?.id)return;
    setSavingClinicSettings(true);
    const payload={
      user_id:session.user.id,
      clinic_name:clinicSettings.clinic_name,
      owner_name:clinicSettings.owner_name,
      phone:clinicSettings.phone,
      email:clinicSettings.email,
      address:clinicSettings.address,
      signature_name:clinicSettings.signature_name,
      designation:clinicSettings.designation,
      signature_image_url:clinicSettings.signature_image_url,
      updated_at:new Date().toISOString(),
    };
    const{error}=await supabase.from("clinic_settings").upsert(payload,{onConflict:"user_id"});
    if(error){notify("Saving clinic settings failed: "+error.message,"error");setSavingClinicSettings(false);return;}
    notify("Clinic settings saved ✓");
    setSavingClinicSettings(false);
  }

  async function uploadSignatureImage(){
    if(!session?.user?.id||!signatureFile){notify("Please choose a signature image first","error");return;}
    setUploadingSignature(true);
    const fileName=`${Date.now()}-${signatureFile.name}`;
    const filePath=`${session.user.id}/${fileName}`;
    const{error:uploadError}=await supabase.storage.from("user-signatures").upload(filePath,signatureFile,{upsert:false});
    if(uploadError){notify("Signature upload failed: "+uploadError.message,"error");setUploadingSignature(false);return;}
    const{error:saveError}=await supabase.from("clinic_settings").upsert({
      user_id:session.user.id,
      clinic_name:clinicSettings.clinic_name,
      owner_name:clinicSettings.owner_name,
      phone:clinicSettings.phone,
      email:clinicSettings.email,
      address:clinicSettings.address,
      signature_name:clinicSettings.signature_name,
      designation:clinicSettings.designation,
      signature_image_url:filePath,
      updated_at:new Date().toISOString(),
    },{onConflict:"user_id"});
    if(saveError){notify("Saving signature path failed: "+saveError.message,"error");setUploadingSignature(false);return;}
    setClinicSettings((s)=>({...s,signature_image_url:filePath}));
    notify("Signature uploaded ✓");
    setSignatureFile(null);
    setUploadingSignature(false);
  }

  // ✅ FIXED - Removed duplicate, increased min length to 8
  async function handleChangePassword(){
    setPasswordMessage("");
    if(!newPassword||!confirmNewPassword){notify("Please fill in both password fields","error");return;}
    if(newPassword.length<8){notify("Password must be at least 8 characters","error");return;}
    if(newPassword!==confirmNewPassword){notify("Passwords do not match","error");return;}
    setChangingPassword(true);
    const{error}=await supabase.auth.updateUser({password:newPassword});
    if(error){notify("Password update failed: "+error.message,"error");setChangingPassword(false);return;}
    notify("Password updated successfully ✓");
    setPasswordMessage("Password updated successfully.");
    setNewPassword("");
    setConfirmNewPassword("");
    setChangingPassword(false);
  }

  useEffect(()=>{
    if(!session?.user?.id)return;
    async function init(){
      setReady(false);
      await Promise.all([fetchPatients(),fetchTherapists(),fetchClinicSettings()]);
      setReady(true);
    }
    init();
  },[session,fetchPatients,fetchTherapists,fetchClinicSettings]);

  const stats={
    total:patients.length,
    active:patients.filter((p)=>p.status==="Active").length,
    completed:patients.filter((p)=>p.status==="Completed").length,
    todayAppts:patients.filter((p)=>p.nextVisit===TODAY).length,
    pendingPay:patients.filter((p)=>p.payments==="Pending").length,
    revenue:patients.reduce((s,p)=>s+(p.amount||0),0),
  };

  // ✅ FIXED - Use basePlanName from usePlan hook
  const canAddPatient=patientLimit==null||patients.length<patientLimit;
  const canAddTherapist=therapistLimit==null||therapists.length<therapistLimit;

  const filtered=patients.filter((p)=>{
    const q=search.toLowerCase();
    const matchSearch=!q||(p.name||"").toLowerCase().includes(q)||(p.patientId||"").toLowerCase().includes(q)||(p.condition||"").toLowerCase().includes(q)||(p.phone||"").includes(q);
    const matchStatus=filterStatus==="All"||p.status===filterStatus;
    const matchTherapist=filterTherapist==="All"||p.therapist===filterTherapist;
    return matchSearch&&matchStatus&&matchTherapist;
  }).sort((a,b)=>{
    if(sortBy==="name")return(a.name||"").localeCompare(b.name||"");
    if(sortBy==="date")return b.joinDate>a.joinDate?1:-1;
    return b.sessions-a.sessions;
  });

  function openAddPatient(){
    if(!canAddPatient){openUpgrade(`Your plan allows up to ${patientLimit} patients. Upgrade to add more.`);return;}
    setPatientForm({...patientDraft});
    setEditPatientId(null);
    setShowPatientForm(true);
  }

  function openEditPatient(p){
    setPatientForm({...p,bp:p.vitals?.bp||"",pulse:p.vitals?.pulse||"",weight:p.vitals?.weight||""});
    setEditPatientId(p.id);
    setShowPatientForm(true);
  }

  async function handleSavePatient(){
    if(!patientForm.name||!patientForm.age||!patientForm.gender||!patientForm.phone||!patientForm.condition||!patientForm.therapist||patientForm.painScore===""||patientForm.painScore===null||patientForm.painScore===undefined||!patientForm.nextVisit||(patientForm.condition==="Other"&&!patientForm.customCondition?.trim())){
      notify("Please fill in all required fields","error");return;
    }
    setSavingPatient(true);
    const payload={...patientToDb(patientForm),user_id:session.user.id};
    if(editPatientId){
      const{error}=await supabase.from("patients").update(payload).eq("id",editPatientId);
      if(error){notify("Save failed: "+error.message,"error");setSavingPatient(false);return;}
      notify("Patient record updated ✓");
    }else{
      const{error}=await supabase.from("patients").insert([payload]);
      if(error){notify("Save failed: "+error.message,"error");setSavingPatient(false);return;}
      notify("Patient registered ✓");
      clearPatientDraft();
    }
    setSavingPatient(false);
    setShowPatientForm(false);
    setEditPatientId(null);
    setPatientForm({...EMPTY_PATIENT});
    fetchPatients();
  }

  async function handleDeletePatient(id){
    const{error}=await supabase.from("patients").delete().eq("id",id);
    if(error){notify("Delete failed: "+error.message,"error");return;}
    setConfirmDeleteId(null);
    setSelected(null);
    notify("Patient record deleted","error");
    fetchPatients();
  }

  async function recordSession(id){
    const pt=patients.find((p)=>p.id===id);
    if(!pt)return;
    const newCount=Math.min(pt.sessions+1,pt.totalSessions);
    const newEntry=`${TODAY}: Session ${newCount} completed`;
    const newHistory=[newEntry,...(pt.history||[])];
    const{error}=await supabase.from("patients").update({sessions:newCount,history:newHistory}).eq("id",id);
    if(error){notify("Error: "+error.message,"error");return;}
    notify("Session recorded ✓");
    fetchPatients();
    if(selected&&selected.id===id)setSelected((s)=>({...s,sessions:newCount,history:newHistory}));
  }

  async function addHistoryEntry(id){
    if(!historyInput.trim())return;
    const pt=patients.find((p)=>p.id===id);
    if(!pt)return;
    const newEntry=`${TODAY}: ${historyInput.trim()}`;
    const newHistory=[newEntry,...(pt.history||[])];
    const{error}=await supabase.from("patients").update({history:newHistory}).eq("id",id);
    if(error){notify("Error: "+error.message,"error");return;}
    setHistoryInput("");
    notify("Note added ✓");
    fetchPatients();
    setSelected((s)=>({...s,history:newHistory}));
  }

  function openAddTherapist(){
    if(!canAddTherapist){openUpgrade(`Your plan allows up to ${therapistLimit} therapists. Upgrade to add more.`);return;}
    setTherapistForm({...EMPTY_THERAPIST});
    setEditTherapistId(null);
    setShowTherapistForm(true);
  }

  function openEditTherapist(t){
    setTherapistForm({name:t.name,specialty:t.specialty||"",avatar:t.avatar||""});
    setEditTherapistId(t.id);
    setShowTherapistForm(true);
  }

  async function handleSaveTherapist(){
    if(!therapistForm.name.trim()){notify("Therapist name is required","error");return;}
    setSavingTherapist(true);
    const avatarVal=therapistForm.avatar.trim()||autoAvatar(therapistForm.name);
    const payload={name:therapistForm.name.trim(),specialty:therapistForm.specialty.trim(),avatar:avatarVal,user_id:session.user.id};
    if(editTherapistId){
      const{error}=await supabase.from("therapists").update(payload).eq("id",editTherapistId);
      if(error){notify("Save failed: "+error.message,"error");setSavingTherapist(false);return;}
      notify("Therapist updated ✓");
    }else{
      const{error}=await supabase.from("therapists").insert([payload]);
      if(error){notify("Save failed: "+error.message,"error");setSavingTherapist(false);return;}
      notify("Therapist added ✓");
    }
    setSavingTherapist(false);
    setShowTherapistForm(false);
    setEditTherapistId(null);
    setTherapistForm({...EMPTY_THERAPIST});
    fetchTherapists();
  }

  async function handleDeleteTherapist(id){
    const t=therapists.find((x)=>x.id===id);
    const assigned=patients.filter((p)=>p.therapist===t?.name).length;
    if(assigned>0){notify(`Cannot delete — ${assigned} patient(s) assigned to this therapist`,"error");setConfirmDeleteTherapistId(null);return;}
    const{error}=await supabase.from("therapists").delete().eq("id",id);
    if(error){notify("Delete failed: "+error.message,"error");return;}
    setConfirmDeleteTherapistId(null);
    notify("Therapist removed","error");
    fetchTherapists();
  }

  function navigate(p){setPage(p);setSelected(null);setShowPatientForm(false);setSidebarOpen(false);}

  function updatePatientForm(k,v){
    setPatientForm((f)=>{
      const updated={...f,[k]:v};
      if(!editPatientId)setPatientDraft(updated);
      return updated;
    });
  }

  function updateTherapistForm(k,v){setTherapistForm((f)=>({...f,[k]:v}));}

  function formatHomeProgrammeForWhatsApp(patient,programme,clinicName){
    return encodeURIComponent(
      `Home Exercise Programme for ${patient.name}\n\nProgramme: ${programme.programme_title||"Not specified"}\n\nCondition: ${programme.condition||"Not specified"}\n\nGoal:\n${programme.goal||"Not specified"}\n\nExercises:\n${programme.exercise_list||"Not specified"}\n\nPrecautions:\n${programme.precautions||"Not specified"}\n\nPatient Advice:\n${programme.patient_advice||"Not specified"}\n\nVideo Link:\n${programme.video_link||"Not provided"}\n\n— ${clinicName||"Your Clinic"}`
    );
  }

  async function saveDiagnosticDetails(){
    if(!selected?.id)return;
    setSavingDiagnostics(true);
    const{error}=await supabase.from("patients").update({diagnostic_tools:selected.diagnosticTools||"",outcome_measures:selected.outcomeMeasures||"",special_tests:selected.specialTests||"",notes:selected.notes||""}).eq("id",selected.id);
    if(error){notify("Saving diagnostics failed: "+error.message,"error");setSavingDiagnostics(false);return;}
    notify("Diagnostic details saved ✓");
    setSavingDiagnostics(false);
    fetchPatients();
  }

  async function savePatientHistories(){
    if(!selected?.id)return;
    setSavingDiagnostics(true);
    const{error}=await supabase.from("patients").update({past_history:selected.pastHistory||"",medical_history:selected.medicalHistory||"",surgical_history:selected.surgicalHistory||""}).eq("id",selected.id);
    if(error){notify("Saving histories failed: "+error.message,"error");setSavingDiagnostics(false);return;}
    notify("Patient histories saved ✓");
    setSavingDiagnostics(false);
    fetchPatients();
  }

  async function saveHomeProgramme(){
    if(!selected?.id||!session?.user?.id)return;
    setSavingHomeProgramme(true);
    const payload={
      patient_id:selected.id,user_id:session.user.id,
      programme_title:homeProgramme.programme_title||"",
      condition:homeProgramme.condition||"",
      goal:homeProgramme.goal||"",
      exercise_list:homeProgramme.exercise_list||"",
      precautions:homeProgramme.precautions||"",
      patient_advice:homeProgramme.patient_advice||"",
      video_link:homeProgramme.video_link||"",
      updated_at:new Date().toISOString(),
    };
    const{error}=await supabase.from("patient_home_programmes").upsert(payload,{onConflict:"patient_id"});
    if(error){notify("Saving home programme failed: "+error.message,"error");setSavingHomeProgramme(false);return;}
    notify("Home Exercise Programme saved ✓");
    setSavingHomeProgramme(false);
    setEditingHomeProgramme(false);
  }

  async function deleteHomeProgramme(){
    if(!selected?.id)return;
    if(!window.confirm("Delete this Home Exercise Programme?"))return;
    const{error}=await supabase.from("patient_home_programmes").delete().eq("patient_id",selected.id);
    if(error){notify("Deleting home programme failed: "+error.message,"error");return;}
    notify("Home Exercise Programme deleted ✓");
    setHomeProgramme({programme_title:"",condition:"",goal:"",exercise_list:"",precautions:"",patient_advice:"",video_link:""});
    setEditingHomeProgramme(false);
  }

  async function uploadPatientAttachment(){
    if(!selected?.id||!session?.user?.id||!attachmentFile){notify("Please choose a file first","error");return;}
    setUploadingAttachment(true);
    const fileExt=attachmentFile.name.split(".").pop();
    const fileName=`${Date.now()}-${attachmentFile.name}`;
    const filePath=`${session.user.id}/${selected.id}/${fileName}`;
    const{error:uploadError}=await supabase.storage.from("patient-attachments").upload(filePath,attachmentFile,{upsert:false});
    if(uploadError){notify("File upload failed: "+uploadError.message,"error");setUploadingAttachment(false);return;}
    const{error:insertError}=await supabase.from("patient_attachments").insert([{patient_id:selected.id,user_id:session.user.id,file_name:attachmentFile.name,file_url:filePath,file_type:fileExt||attachmentFile.type||"file"}]);
    if(insertError){notify("Saving attachment record failed: "+insertError.message,"error");setUploadingAttachment(false);return;}
    await fetchPatientAttachments(selected.id);
    notify("Attachment uploaded ✓");
    setAttachmentFile(null);
    setUploadingAttachment(false);
  }

  async function fetchPatientAttachments(patientId){
    if(!patientId||!session?.user?.id)return;
    setLoadingAttachments(true);
    const{data,error}=await supabase.from("patient_attachments").select("*").eq("patient_id",patientId).eq("user_id",session.user.id).order("created_at",{ascending:false});
    if(error){notify("Loading attachments failed: "+error.message,"error");setLoadingAttachments(false);return;}
    setPatientAttachments(data||[]);
    setLoadingAttachments(false);
  }

  async function fetchHomeProgramme(patientId){
    if(!patientId||!session?.user?.id)return;
    const{data,error}=await supabase.from("patient_home_programmes").select("*").eq("patient_id",patientId).eq("user_id",session.user.id).maybeSingle();
    if(error){notify("Loading home programme failed: "+error.message,"error");return;}
    if(data){
      setHomeProgramme({
        programme_title:data.programme_title||"",
        condition:data.condition||"",
        goal:data.goal||"",
        exercise_list:data.exercise_list||"",
        precautions:data.precautions||"",
        patient_advice:data.patient_advice||"",
        video_link:data.video_link||"",
      });
    }else{
      setHomeProgramme({programme_title:"",condition:"",goal:"",exercise_list:"",precautions:"",patient_advice:"",video_link:""});
    }
  }

  async function deletePatientAttachment(file){
    if(!file?.id||!file?.file_url)return;
    if(!window.confirm(`Delete attachment "${file.file_name}"?`))return;
    const{error:storageError}=await supabase.storage.from("patient-attachments").remove([file.file_url]);
    if(storageError){notify("Deleting file failed: "+storageError.message,"error");return;}
    const{error:dbErr}=await supabase.from("patient_attachments").delete().eq("id",file.id);
    if(dbErr){notify("Deleting attachment record failed: "+dbErr.message,"error");return;}
    notify("Attachment deleted ✓");
    fetchPatientAttachments(selected.id);
  }

  useEffect(()=>{
    supabase.auth.getSession().then(({data:{session}})=>{setSession(session);});
    const{data:listener}=supabase.auth.onAuthStateChange((_event,session)=>{
      setSession(session);
      if(!session){setPatients([]);setTherapists([]);setReady(false);}
    });
    return()=>{listener?.subscription?.unsubscribe?.();};
  },[]);

  useEffect(()=>{
    if(selected?.id&&detailTab==="diagnostics")fetchPatientAttachments(selected.id);
  },[selected,detailTab]);

  useEffect(()=>{
    if(selected?.id&&detailTab==="home exercise programme")fetchHomeProgramme(selected.id);
  },[selected,detailTab]);

  // ✅ FIXED - Reset password route
  if(window.location.pathname==="/reset-password"){return<ResetPassword/>;}

  if(!session)return<Auth/>;

  // ✅ FIXED - Now shows beautiful UpgradeWall instead of plain buttons
  if(subscriptionRequired&&!isAdmin){
    return(
      <div style={{background:T.bg,minHeight:"100vh",fontFamily:"'DM Sans',sans-serif"}}>
        <style>{GLOBAL_CSS}</style>
        <UpgradeWall
          featureKey={null}
          trialDaysLeft={0}
          isTrialExpired={true}
          currentPlan={basePlanName}
          session={session}
          onClose={null}
          onUpgradeSuccess={(planKey)=>{
            notify(`Upgraded to ${planKey}! Refreshing...`);
            setTimeout(()=>window.location.reload(),1500);
          }}
        />
        <div style={{position:"fixed",bottom:24,left:"50%",transform:"translateX(-50%)",zIndex:600}}>
          <button
            className="btn btn-outline btn-sm"
            onClick={()=>supabase.auth.signOut()}
            style={{fontSize:12,color:T.textDim,background:T.panel,border:`1px solid ${T.border}`}}
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  if(!ready){
    return(
      <div style={{background:T.bg,minHeight:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:20,fontFamily:"'DM Sans',sans-serif"}}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{width:52,height:52,border:`2px solid ${T.gold}`,borderTopColor:"transparent",borderRadius:"50%",animation:"spin 0.9s linear infinite"}}/>
        <div style={{fontFamily:"'Cormorant Garamond',serif",color:T.gold,fontSize:24,letterSpacing:3}}>PhysioCens</div>
        <div style={{color:T.textDim,fontSize:12,letterSpacing:1.5}}>CONNECTING TO DATABASE…</div>
      </div>
    );
  }

  if(dbError){
    return(
      <div style={{background:T.bg,minHeight:"100vh",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8,fontFamily:"'DM Sans',sans-serif",padding:32,textAlign:"center"}}>
        <div style={{fontSize:48}}>⚠️</div>
        <div style={{color:"#f87171",fontSize:18,fontWeight:600}}>Database Connection Failed</div>
        <div style={{color:T.textMid,fontSize:13,maxWidth:480}}>Check your .env file has the correct Supabase URL and anon key.</div>
        <div style={{color:T.textDim,fontSize:11}}>{dbError}</div>
        <button className="btn btn-gold" onClick={()=>{setDbError(null);setReady(false);Promise.all([fetchPatients(),fetchTherapists()]).then(()=>setReady(true));}}>Retry Connection</button>
      </div>
    );
  }

  const deletePatientTarget=confirmDeleteId?patients.find((p)=>p.id===confirmDeleteId):null;
  const deleteTherapistTarget=confirmDeleteTherapistId?therapists.find((t)=>t.id===confirmDeleteTherapistId):null;

  const sidebarStyle={
    width:252,background:T.panel,borderRight:`1px solid ${T.border}`,
    display:"flex",flexDirection:"column",height:"100vh",flexShrink:0,overflowY:"auto",
    position:isMobile?"fixed":"sticky",top:0,
    left:isMobile?(sidebarOpen?0:-260):0,
    zIndex:isMobile?200:"auto",transition:"left 0.25s ease",
  };

  const mainContentStyle={
    flex:1,minWidth:0,overflowY:"auto",overflowX:"hidden",
    width:isMobile?"100vw":"auto",maxWidth:isMobile?"100vw":"none",
    paddingTop:isMobile?52:0,marginLeft:0,
  };

  return(
    <div style={{background:T.bg,minHeight:"100vh",color:T.text,fontFamily:"'DM Sans',sans-serif",display:"flex"}}>
      <style>{GLOBAL_CSS}</style>

      {isMobile&&(
        <button onClick={()=>setSidebarOpen(true)} style={{position:"fixed",top:12,left:12,zIndex:300,background:T.card,border:`1px solid ${T.border}`,borderRadius:8,padding:"7px 11px",cursor:"pointer",color:T.gold,fontSize:20,lineHeight:1,display:"flex",alignItems:"center",justifyContent:"center"}}>☰</button>
      )}

      {isMobile&&sidebarOpen&&(
        <div onClick={()=>setSidebarOpen(false)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.65)",zIndex:199}}/>
      )}

      {toast&&(
        <div style={{position:"fixed",bottom:24,right:24,zIndex:300,background:toast.type==="error"?"rgba(20,8,8,0.97)":"rgba(7,18,12,0.97)",border:`1px solid ${toast.type==="error"?"#f87171":T.active}`,borderRadius:9,padding:"13px 20px",fontSize:13,color:toast.type==="error"?"#f87171":T.activeText,display:"flex",alignItems:"center",gap:10,boxShadow:"0 8px 40px rgba(0,0,0,0.5)",animation:"fadeUp 0.3s ease"}}>
          <span>{toast.type==="error"?"⚠":"✓"}</span>{toast.msg}
        </div>
      )}

      {/* ✅ FIXED - UpgradeWall replaces old Gmail modal */}
      {showUpgrade&&(
        <UpgradeWall
          featureKey={upgradeWallFeature}
          trialDaysLeft={trialDaysLeft}
          isTrialExpired={isTrialExpired}
          currentPlan={basePlanName}
          session={session}
          onClose={()=>{setShowUpgrade(false);setUpgradeWallFeature(null);}}
          onUpgradeSuccess={(planKey)=>{
            setShowUpgrade(false);
            notify(`✅ Upgraded to ${planKey}!`);
            setTimeout(()=>window.location.reload(),1500);
          }}
        />
      )}

      {confirmDeleteId&&deletePatientTarget&&(
        <div className="modal-backdrop" onClick={()=>setConfirmDeleteId(null)}>
          <div onClick={(e)=>e.stopPropagation()} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:36,width:420,textAlign:"center"}}>
            <div style={{fontSize:44,marginBottom:14}}>⚠️</div>
            <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:22,fontWeight:600,marginBottom:8}}>Delete Patient Record?</div>
            <div style={{color:T.textMid,fontSize:13,lineHeight:1.7,marginBottom:28}}>Permanently deletes <strong style={{color:T.text}}>{deletePatientTarget.name}</strong>'s complete record. Cannot be undone.</div>
            <div style={{display:"flex",gap:12,justifyContent:"center"}}>
              <button className="btn btn-outline" onClick={()=>setConfirmDeleteId(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={()=>handleDeletePatient(confirmDeleteId)}>Delete Permanently</button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteTherapistId&&deleteTherapistTarget&&(
        <div className="modal-backdrop" onClick={()=>setConfirmDeleteTherapistId(null)}>
          <div onClick={(e)=>e.stopPropagation()} style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,padding:36,width:420,textAlign:"center"}}>
            <div style={{fontSize:44,marginBottom:14}}>⚠️</div>
            <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:22,fontWeight:600,marginBottom:8}}>Remove Therapist?</div>
            <div style={{color:T.textMid,fontSize:13,lineHeight:1.7,marginBottom:28}}>Removes <strong style={{color:T.text}}>{deleteTherapistTarget.name}</strong> from the system.</div>
            <div style={{display:"flex",gap:12,justifyContent:"center"}}>
              <button className="btn btn-outline" onClick={()=>setConfirmDeleteTherapistId(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={()=>handleDeleteTherapist(confirmDeleteTherapistId)}>Remove Therapist</button>
            </div>
          </div>
        </div>
      )}

      {/* ── SIDEBAR ── */}
      <div style={sidebarStyle}>
        <div style={{padding:isMobile?"8px 12px":"16px 16px 14px",borderBottom:`1px solid ${T.border}`,flexShrink:0,display:"block"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
            <div style={{width:28,height:28,flexShrink:0,borderRadius:8,backgroundImage:"url('/logo.png')",backgroundSize:"220%",backgroundPosition:"center",backgroundRepeat:"no-repeat",overflow:"hidden"}}/>
            <div style={{minWidth:0,flex:1}}>
              <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:isMobile?14:17,fontWeight:700,color:T.gold,letterSpacing:"0.2px",lineHeight:1.1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                {clinicSettings.clinic_name?.trim()||"PhysioCens"}
              </div>
              <div style={{fontSize:9,color:T.textDim,letterSpacing:"0.8px",textTransform:"uppercase",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginTop:2}}>
                {clinicSettings.owner_name?.trim()||""}
              </div>
            </div>
          </div>
        </div>

        <nav style={{padding:"10px 10px",flex:1,overflowY:"auto",minHeight:0}}>
          <div style={{fontSize:9,color:T.textDim,letterSpacing:"1.4px",textTransform:"uppercase",padding:"0 4px",marginBottom:6,fontWeight:700}}>Navigation</div>
          {[
            {id:"dashboard",icon:"◈",label:"Dashboard"},
            {id:"patients",icon:"◉",label:"Patients"},
            {id:"therapists",icon:"✚",label:"Therapists"},
            {id:"schedule",icon:"◷",label:"Schedule"},
            {id:"analytics",icon:"◎",label:"Analytics"},
            {id:"knowledge",icon:"🧠",label:"Knowledge Hub"},
            {id:"research",icon:"🔬",label:"Research"},
            {id:"settings",icon:"⚙",label:"Settings"},
          ].map((nav)=>(
            <div
              key={nav.id}
              className={`nav-link${page===nav.id?" active":""}`}
              onClick={()=>navigate(nav.id)}
            >
              <span style={{fontSize:15,width:18,textAlign:"center",flexShrink:0}}>{nav.icon}</span>
              <span style={{flex:1}}>{nav.label}</span>
            </div>
          ))}

          {therapists.length>0&&(
            <>
              <div style={{fontSize:9,color:T.textDim,letterSpacing:"1.4px",textTransform:"uppercase",padding:"0 4px",marginBottom:6,marginTop:16,fontWeight:700}}>Therapists ({therapists.length})</div>
              {therapists.map((t)=>{
                const activeCount=patients.filter((p)=>p.therapist===t.name&&p.status==="Active").length;
                return(
                  <div key={t.id} style={{display:"flex",alignItems:"center",gap:8,padding:"8px",borderRadius:8,marginBottom:4,background:"rgba(26,37,64,0.4)"}}>
                    <div style={{width:28,height:28,borderRadius:"50%",background:getAvatarColor(t.name),display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:700,color:"#fff",flexShrink:0}}>{t.avatar||getInitials(t.name)}</div>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:11,color:T.text,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.name}</div>
                      <div style={{fontSize:10,color:T.activeText}}>{activeCount} active</div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </nav>

        <div style={{padding:isMobile?"8px 10px":"12px 16px",borderTop:`1px solid ${T.border}`,flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",gap:7,fontSize:11,marginBottom:8}}>
            <span style={{fontSize:8,color:T.active}}>●</span>
            <span style={{color:T.textDim}}>Connected to Supabase</span>
          </div>
          <div style={{background:"rgba(201,169,110,0.08)",border:`1px solid ${T.border}`,borderRadius:8,padding:"6px 10px"}}>
            <div style={{fontSize:10,color:T.textDim,letterSpacing:1,textTransform:"uppercase",fontWeight:700}}>Current Plan</div>
            <div style={{marginTop:4,color:T.goldLight,fontWeight:700,fontSize:13}}>
              {/* ✅ FIXED - Correct plan name display */}
              {planLoading?"Loading...":planName==="free_trial"?"Free Trial":planName==="admin"?"Admin":planName?planName.toUpperCase():"FREE"}
            </div>
            {/* ✅ FIXED - Show limits only when on trial or free */}
            {(planName==="free"||planName==="free_trial")&&patientLimit!=null&&(
              <div style={{marginTop:6,fontSize:11,color:T.textDim,lineHeight:1.5}}>
                <div>Patients: {Math.max(patientLimit-patients.length,0)}/{patientLimit} left</div>
                {therapistLimit!=null&&<div>Therapists: {Math.max(therapistLimit-therapists.length,0)}/{therapistLimit} left</div>}
              </div>
            )}
            {/* ✅ FIXED - Upgrade button uses UpgradeWall not Gmail */}
            {!isAdmin&&(planName==="free"||subscriptionRequired)&&(
              <button
                className="btn btn-gold btn-sm"
                style={{width:"100%",marginTop:8}}
                onClick={()=>openUpgrade("Upgrade to unlock all features.")}
              >
                ⚡ Upgrade Plan
              </button>
            )}
          </div>
          <button className="btn btn-outline btn-sm" style={{width:"100%",marginTop:8}} onClick={()=>supabase.auth.signOut()}>Sign Out</button>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={mainContentStyle}>

        {page==="dashboard"&&(
          <div style={{padding:"30px 16px"}} className="fade-up">
            <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:30}}>
              <div>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:32,fontWeight:600}}>{getGreeting()}</div>
                <div style={{color:T.textMid,fontSize:14,marginTop:5}}>{new Date().toLocaleDateString("en-IN",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</div>
              </div>
              <button className="btn btn-gold" onClick={openAddPatient}>+ Register Patient</button>
            </div>

            {(clinicSettings.clinic_name||clinicSettings.owner_name||clinicSettings.phone||clinicSettings.email)&&(
              <div className="card" style={{padding:18,marginBottom:20}}>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:20,fontWeight:700,color:T.goldLight,marginBottom:8}}>{clinicSettings.clinic_name||"Clinic Profile"}</div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:12,fontSize:13,color:T.textMid}}>
                  <div><strong style={{color:T.text}}>Clinical Incharge:</strong> {clinicSettings.owner_name||"—"}</div>
                  <div><strong style={{color:T.text}}>Phone:</strong> {clinicSettings.phone||"—"}</div>
                  <div><strong style={{color:T.text}}>Email:</strong> {clinicSettings.email||"—"}</div>
                  <div><strong style={{color:T.text}}>Address:</strong> {clinicSettings.address||"—"}</div>
                </div>
              </div>
            )}

            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit, minmax(240px, 1fr))",gap:14,marginBottom:26}}>
              {[
                {label:"Total Patients",val:stats.total,sub:"All registrations",col:T.gold,icon:"◉"},
                {label:"Active Cases",val:stats.active,sub:"In treatment",col:T.active,icon:"▲"},
                {label:"Completed",val:stats.completed,sub:"Discharged",col:"#818cf8",icon:"✓"},
                {label:"Today's Appointments",val:stats.todayAppts,sub:"Scheduled today",col:"#22d3ee",icon:"◷"},
                {label:"Pending Payments",val:stats.pendingPay,sub:"Awaiting settlement",col:T.moderate,icon:"₹"},
                {label:"Total Revenue",val:`₹${stats.revenue.toLocaleString()}`,sub:"Lifetime collected",col:"#a78bfa",icon:"◈"},
              ].map((s)=>(
                <div key={s.label} className="stat-card" style={{"--accent-color":s.col}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                    <div>
                      <div style={{fontSize:10,color:T.textDim,fontWeight:700,letterSpacing:"0.8px",textTransform:"uppercase",marginBottom:9}}>{s.label}</div>
                      <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:36,fontWeight:700,color:s.col,lineHeight:1}}>{s.val}</div>
                      <div style={{fontSize:11,color:T.textDim,marginTop:6}}>{s.sub}</div>
                    </div>
                    <div style={{fontSize:26,color:s.col,opacity:0.2}}>{s.icon}</div>
                  </div>
                </div>
              ))}
            </div>

            <div style={{display:"grid",gridTemplateColumns:"minmax(0, 1.55fr) minmax(0, 1fr)",gap:20}}>
              <div className="card" style={{padding:24}}>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:19,fontWeight:600,color:T.goldLight,marginBottom:16}}>Patient Overview</div>
                {patients.length===0&&<div style={{color:T.textDim,textAlign:"center",padding:28,fontSize:13}}>No patients registered yet.</div>}
                {patients.slice(0,8).map((p)=>(
                  <div key={p.id} className="row-hover" style={{display:"flex",alignItems:"center",gap:13,padding:"12px 6px",borderBottom:`1px solid ${T.border}`,cursor:"pointer",borderRadius:6}}
                    onClick={()=>{setSelected(p);setPage("patients");setDetailTab("overview");fetchPatientAttachments(p.id);}}>
                    <Avatar name={p.name} size={40}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:600,fontSize:14}}>{p.name}</div>
                      <div style={{fontSize:11,color:T.textMid,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{getDisplayCondition(p)}</div>
                    </div>
                    <div style={{flexShrink:0,textAlign:"right"}}>
                      <StatusBadge status={p.status}/>
                      <div style={{fontSize:10,color:T.textDim,marginTop:4}}>{p.sessions}/{p.totalSessions} sessions</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="card" style={{padding:24}}>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:19,fontWeight:600,color:T.goldLight,marginBottom:16}}>Therapist Workload</div>
                {therapists.length===0?(
                  <div style={{color:T.textDim,fontSize:13,textAlign:"center",padding:20}}>No therapists added yet.<div style={{marginTop:12}}><button className="btn btn-outline btn-sm" onClick={()=>navigate("therapists")}>+ Add Therapists</button></div></div>
                ):therapists.map((t)=>{
                  const active=patients.filter((p)=>p.therapist===t.name&&p.status==="Active").length;
                  const total=patients.filter((p)=>p.therapist===t.name).length;
                  return(
                    <div key={t.id} style={{marginBottom:22}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:9}}>
                        <div style={{width:34,height:34,borderRadius:"50%",background:getAvatarColor(t.name),display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:"#fff",flexShrink:0}}>{t.avatar||getInitials(t.name)}</div>
                        <div style={{flex:1}}><div style={{fontSize:12,fontWeight:600,lineHeight:1.3}}>{t.name}</div><div style={{fontSize:10,color:T.textDim}}>{t.specialty}</div></div>
                        <div style={{fontSize:13,fontWeight:700,color:T.gold}}>{active}<span style={{color:T.textDim,fontWeight:400,fontSize:11}}>/{total}</span></div>
                      </div>
                      <ProgressBar pct={total?Math.min((active/Math.max(total,1))*100,100):0}/>
                    </div>
                  );
                })}
                <Divider my={18}/>
                <button className="btn btn-gold" style={{width:"100%"}} onClick={openAddPatient}>+ Register New Patient</button>
              </div>
            </div>
          </div>
        )}

        {page==="therapists"&&(
          <div style={{padding:"30px 16px"}} className="fade-up">
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:24}}>
              <div>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:28,fontWeight:600}}>Manage Therapists</div>
                <div style={{color:T.textMid,fontSize:12,marginTop:3}}>{therapists.length} therapist{therapists.length!==1?"s":""} · Add, edit or remove anytime</div>
              </div>
              <button className="btn btn-gold" onClick={openAddTherapist}>+ Add Therapist</button>
            </div>
            {therapists.length===0?(
              <div className="card" style={{padding:60,textAlign:"center"}}>
                <div style={{fontSize:48,marginBottom:16}}>👨‍⚕️</div>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:22,color:T.goldLight,marginBottom:8}}>No Therapists Yet</div>
                <div style={{color:T.textDim,fontSize:13,marginBottom:24}}>Add your first therapist to start assigning patients.</div>
                <button className="btn btn-gold" onClick={openAddTherapist}>+ Add First Therapist</button>
              </div>
            ):(
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
                {therapists.map((t)=>{
                  const totalPts=patients.filter((p)=>p.therapist===t.name).length;
                  const activePts=patients.filter((p)=>p.therapist===t.name&&p.status==="Active").length;
                  const revenue=patients.filter((p)=>p.therapist===t.name).reduce((s,p)=>s+(p.amount||0),0);
                  return(
                    <div key={t.id} className="card" style={{padding:24}}>
                      <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:18}}>
                        <div style={{width:56,height:56,borderRadius:"50%",background:getAvatarColor(t.name),display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,fontWeight:700,color:"#fff",flexShrink:0}}>{t.avatar||getInitials(t.name)}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:15,fontWeight:700,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.name}</div>
                          <div style={{fontSize:11,color:T.textDim,marginTop:2}}>{t.specialty||"No specialty set"}</div>
                        </div>
                      </div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:18}}>
                        {[["Total",totalPts,T.gold],["Active",activePts,T.active],["Revenue",`₹${revenue.toLocaleString()}`,"#a78bfa"]].map(([label,val,col])=>(
                          <div key={label} style={{background:T.panel,borderRadius:8,padding:"10px 8px",textAlign:"center"}}>
                            <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:20,fontWeight:700,color:col}}>{val}</div>
                            <div style={{fontSize:9,color:T.textDim,marginTop:2,textTransform:"uppercase",letterSpacing:"0.5px"}}>{label}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{marginBottom:18}}>
                        <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:T.textDim,marginBottom:5}}>
                          <span>Active / Total patients</span>
                          <span style={{color:T.gold,fontWeight:700}}>{totalPts?Math.round((activePts/totalPts)*100):0}%</span>
                        </div>
                        <ProgressBar pct={totalPts?Math.min((activePts/totalPts)*100,100):0}/>
                      </div>
                      <div style={{display:"flex",gap:8}}>
                        <button className="btn btn-outline btn-sm" style={{flex:1}} onClick={()=>openEditTherapist(t)}>✎ Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={()=>setConfirmDeleteTherapistId(t.id)}>Remove</button>
                      </div>
                    </div>
                  );
                })}
                <div className="card row-hover" style={{padding:24,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:"pointer",border:`1px dashed ${T.border}`,minHeight:220}} onClick={openAddTherapist}>
                  <div style={{fontSize:36,color:T.goldDim,marginBottom:12}}>+</div>
                  <div style={{fontSize:13,color:T.textDim,fontWeight:500}}>Add New Therapist</div>
                </div>
              </div>
            )}
          </div>
        )}

        {page==="patients"&&!selected&&(
          <div style={{padding:"30px 16px"}} className="fade-up">
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:24}}>
              <div>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:28,fontWeight:600}}>Patient Registry</div>
                <div style={{color:T.textMid,fontSize:12,marginTop:3}}>{filtered.length} records · 🟢 Live Supabase database</div>
              </div>
              <button className="btn btn-gold" onClick={openAddPatient}>+ New Patient</button>
            </div>
            <div style={{display:"flex",gap:10,marginBottom:20,flexWrap:"wrap"}}>
              <input placeholder="Search name, condition…" value={search} onChange={(e)=>setSearch(e.target.value)} style={{maxWidth:280}}/>
              <select value={filterStatus} onChange={(e)=>setFilterStatus(e.target.value)} style={{width:150}}><option>All</option><option>Active</option><option>Completed</option></select>
              <select value={filterTherapist} onChange={(e)=>setFilterTherapist(e.target.value)} style={{width:230}}><option value="All">All Therapists</option>{therapists.map((t)=><option key={t.id}>{t.name}</option>)}</select>
              <select value={sortBy} onChange={(e)=>setSortBy(e.target.value)} style={{width:160}}><option value="name">Sort: Name</option><option value="date">Sort: Join Date</option><option value="sessions">Sort: Sessions</option></select>
            </div>
            <div className="card" style={{overflow:"hidden"}}>
              <div style={{display:"grid",gridTemplateColumns:"2fr 1.6fr 1.5fr 1fr 0.9fr 0.9fr 110px",padding:"11px 20px",background:T.panel,borderBottom:`1px solid ${T.border}`,fontSize:9,fontWeight:700,letterSpacing:"1.1px",color:T.textDim,textTransform:"uppercase"}}>
                <div>Patient</div><div>Diagnosis</div><div>Therapist</div><div>Progress</div><div>Status</div><div>Payment</div><div>Actions</div>
              </div>
              {filtered.length===0&&<div style={{padding:44,textAlign:"center",color:T.textDim,fontSize:13}}>No patients found.</div>}
              {filtered.map((p)=>(
                <div key={p.id} className="row-hover" style={{display:"grid",gridTemplateColumns:"2fr 1.6fr 1.5fr 1fr 0.9fr 0.9fr 110px",padding:"15px 20px",alignItems:"center",borderBottom:`1px solid ${T.border}`}}>
                  <div style={{display:"flex",alignItems:"center",gap:12,cursor:"pointer"}} onClick={()=>{setSelected(p);setDetailTab("overview");fetchPatientAttachments(p.id);}}>
                    <Avatar name={p.name} size={38}/>
                    <div>
                      <div style={{fontWeight:600,fontSize:14}}>{p.name}</div>
                      <div style={{fontSize:11,color:T.textDim}}>{p.patientId?`${p.patientId} · `:""}{p.age}y · {p.gender}</div>
                    </div>
                  </div>
                  <div>
                    <div style={{fontSize:12,color:T.textMid,marginBottom:2,lineHeight:1.3}}>{getDisplayCondition(p)}</div>
                    <span style={{fontSize:10,fontWeight:700,color:getPainColor(p.painScore)}}>NPRS {p.painScore}/10 · {getPainLabel(p.painScore)}</span>
                  </div>
                  <div style={{fontSize:12,color:T.textMid}}>{p.therapist}</div>
                  <div>
                    <div style={{fontSize:11,color:T.textDim,marginBottom:5}}>{p.sessions}/{p.totalSessions}</div>
                    <ProgressBar pct={getProgress(p)}/>
                  </div>
                  <div><StatusBadge status={p.status}/></div>
                  <div><PaymentBadge payments={p.payments}/></div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    <button className="btn btn-outline btn-sm" onClick={()=>openEditPatient(p)}>✎</button>
                    <button className="btn-session" onClick={()=>recordSession(p.id)}>+1</button>
                    <WhatsAppReminderButton patientName={p.name} phoneNumber={p.phone||""} appointmentTime={p.nextVisit} clinicName={clinicSettings.clinic_name}/>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {page==="patients"&&selected&&(
          <div style={{padding:"30px 16px"}} className="fade-up">
            <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:24}}>
              <button className="btn btn-outline btn-sm" onClick={()=>setSelected(null)}>← Back</button>
              <div style={{flex:1}}>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:27,fontWeight:600}}>{selected.name}</div>
                {/* ✅ FIXED - Removed debug text */}
                <div style={{fontSize:12,color:T.textMid,marginTop:2}}>{selected.patientId?`Patient ID: ${selected.patientId} · `:""}Registered {selected.joinDate}</div>
              </div>
              <button className="btn btn-outline btn-sm" onClick={()=>openEditPatient(selected)}>✎ Edit</button>
              <button className="btn btn-gold btn-sm" onClick={async()=>await printPatientReport(selected,clinicSettings)}>🖨 Print Report</button>
              <button className="btn btn-danger btn-sm" onClick={()=>setConfirmDeleteId(selected.id)}>Delete</button>
            </div>
            <div style={{display:"flex",borderBottom:`1px solid ${T.border}`,marginBottom:24,overflowX:"auto"}}>
              {["overview","vitals","session log",
  "diagnostics","histories",
  "home exercise programme",
  "billing",
  "outcomes","progress","soap ai"]
  .map((t)=>(
                <div key={t} className={`tab${detailTab===t?" active":""}`} onClick={()=>setDetailTab(t)} style={{textTransform:"capitalize",whiteSpace:"nowrap"}}>{t}</div>
              ))}
            </div>

            {detailTab==="overview"&&(
              <div style={{display:"grid",gridTemplateColumns:"320px 1fr",gap:20}}>
                <div>
                  <div className="card" style={{padding:26,marginBottom:14,textAlign:"center"}}>
                    <Avatar name={selected.name} size={76}/>
                    <div style={{marginTop:14,fontFamily:"'Cormorant Garamond',serif",fontSize:23,fontWeight:600}}>{selected.name}</div>
                    <div style={{color:T.textMid,fontSize:13,marginTop:3}}>{selected.age} yrs · {selected.gender}{selected.blood&&selected.blood!=="—"?` · ${selected.blood}`:""}</div>
                    <div style={{display:"flex",justifyContent:"center",gap:7,marginTop:11,flexWrap:"wrap"}}>
                      <StatusBadge status={selected.status}/>
                      <span style={{display:"inline-flex",alignItems:"center",fontSize:10,fontWeight:700,padding:"3px 10px",borderRadius:20,background:"rgba(0,0,0,0.3)",color:getPainColor(selected.painScore)}}>NPRS {selected.painScore}/10 · {getPainLabel(selected.painScore)}</span>
                    </div>
                  </div>
                  <div className="card" style={{padding:20}}>
                    <SectionLabel text="Contact Details"/>
                    {[["📞","Phone",selected.phone],["✉","Email",selected.email],["📍","Address",selected.address]].map(([icon,label,value])=>(
                      <div key={label} style={{display:"flex",gap:10,marginBottom:11,alignItems:"flex-start"}}>
                        <span style={{color:T.textDim,fontSize:13,marginTop:1,flexShrink:0}}>{icon}</span>
                        <div><div style={{fontSize:10,color:T.textDim}}>{label}</div><div style={{fontSize:13,color:value?T.textMid:T.textDim}}>{value||"Not recorded"}</div></div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="card" style={{padding:26,marginBottom:14}}>
                    <SectionLabel text="Treatment Overview"/>
                    <div style={{marginBottom:16}}><div style={{fontSize:11,color:T.textDim,marginBottom:4}}>Diagnosis</div><div style={{fontSize:17,fontWeight:600}}>{getDisplayCondition(selected)}</div></div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:18}}>
                      {[["Therapist",selected.therapist,null],["Joined",selected.joinDate,null],["Next Visit",selected.nextVisit||"Not scheduled",null],["Payment",selected.payments,selected.payments==="Paid"?T.activeText:T.moderate]].map(([label,value,color])=>(
                        <div key={label} style={{background:T.panel,borderRadius:8,padding:"12px 14px"}}>
                          <div style={{fontSize:10,color:T.textDim,marginBottom:3}}>{label}</div>
                          <div style={{fontSize:13,fontWeight:600,color:color||T.text}}>{value}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{marginBottom:16}}>
                      <div style={{display:"flex",justifyContent:"space-between",marginBottom:7}}>
                        <div style={{fontSize:11,color:T.textDim}}>Session Progress</div>
                        <div style={{fontSize:12,fontWeight:700,color:T.gold}}>{selected.sessions}/{selected.totalSessions} ({getProgress(selected)}%)</div>
                      </div>
                      <ProgressBar pct={getProgress(selected)} height={8}/>
                    </div>
                    <div style={{display:"flex",gap:10}}>
                      <button className="btn btn-gold btn-sm" onClick={()=>recordSession(selected.id)}>+ Record Session</button>
                      <button className="btn btn-outline btn-sm" onClick={()=>openEditPatient(selected)}>Update Record</button>
                    </div>
                  </div>
                  {/* ✅ FIXED - Correct plan check using basePlanName */}
                  {(basePlanName==="pro"||basePlanName==="plus"||isTrialActive||isAdmin)?(
                    <div className="card" style={{padding:20}}>
                      <SectionLabel text="Billing Summary"/>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div><div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:30,fontWeight:700,color:T.gold}}>₹{(selected.amount||0).toLocaleString()}</div><div style={{fontSize:11,color:T.textDim,marginTop:3}}>Total billed amount</div></div>
                        <PaymentBadge payments={selected.payments}/>
                      </div>
                    </div>
                  ):(
                    <div className="card" style={{padding:20}}>
                      <SectionLabel text="Billing Summary"/>
                      <div style={{textAlign:"center",padding:20}}>
                        <div style={{fontSize:28,marginBottom:10}}>🔒</div>
                        <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:22,fontWeight:700,color:T.goldLight,marginBottom:8}}>Billing is a Pro Feature</div>
                        <div style={{fontSize:13,color:T.textMid,lineHeight:1.7,marginBottom:14}}>Upgrade to Pro or Plus to unlock billing and payment summaries.</div>
                        <button className="btn btn-gold btn-sm" onClick={()=>openUpgrade("Upgrade to Pro or Plus to unlock billing features.","billing")}>Upgrade</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {detailTab==="vitals"&&(
              <div className="card" style={{padding:30}}>
                <SectionLabel text="Recorded Vitals"/>
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:18}}>
                  {[["Blood Pressure",selected.vitals?.bp||"—","mmHg","♥"],["Pulse Rate",selected.vitals?.pulse||"—","bpm","◉"]].map(([label,value,unit,icon])=>(
                    <div key={label} style={{background:T.panel,borderRadius:10,padding:28,border:`1px solid ${T.border}`,textAlign:"center"}}>
                      <div style={{fontSize:30,color:T.gold,marginBottom:14,opacity:0.7}}>{icon}</div>
                      <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:38,fontWeight:700,color:value!=="—"?T.text:T.textDim}}>{value}</div>
                      <div style={{fontSize:11,color:T.textDim,marginTop:4}}>{unit}</div>
                      <div style={{fontSize:12,color:T.textMid,marginTop:8,fontWeight:500}}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {detailTab==="session log"&&(
              <div className="card" style={{padding:28}}>
                <SectionLabel text="Session History & Clinical Log"/>
                <div style={{display:"flex",gap:10,marginBottom:22}}>
                  <input placeholder="Add session note… press Enter" value={historyInput} onChange={(e)=>setHistoryInput(e.target.value)} onKeyDown={(e)=>{if(e.key==="Enter")addHistoryEntry(selected.id);}}/>
                  <button className="btn btn-gold btn-sm" style={{flexShrink:0,whiteSpace:"nowrap"}} onClick={()=>addHistoryEntry(selected.id)}>Add Entry</button>
                </div>
                {(selected.history||[]).length===0&&<div style={{color:T.textDim,fontSize:13,textAlign:"center",padding:32}}>No history entries yet.</div>}
                {(selected.history||[]).map((entry,i)=>(
                  <div key={i} style={{display:"flex",gap:14,padding:"13px 0",borderBottom:`1px solid ${T.border}`}}>
                    <div style={{width:2,background:T.gold,borderRadius:1,flexShrink:0,minHeight:20}}/>
                    <div style={{fontSize:13,color:T.textMid,lineHeight:1.6}}>{entry}</div>
                  </div>
                ))}
              </div>
            )}

            {detailTab==="diagnostics"&&(
              <div className="card" style={{padding:28}}>
                <SectionLabel text="Diagnostic Details"/>
                <div style={{display:"grid",gridTemplateColumns:"1fr",gap:8}}>
                  <div><FieldLabel>Diagnostic Tools</FieldLabel><textarea rows={3} value={selected.diagnosticTools||""} onChange={(e)=>setSelected((s)=>({...s,diagnosticTools:e.target.value}))} placeholder="MRI, X-ray, CT, lab reports, ultrasound, etc." style={{resize:"vertical"}}/></div>
                  <div><FieldLabel>Outcome Measures</FieldLabel><textarea rows={3} value={selected.outcomeMeasures||""} onChange={(e)=>setSelected((s)=>({...s,outcomeMeasures:e.target.value}))} placeholder="NPRS, ROM, Oswestry, DASH, WOMAC, etc." style={{resize:"vertical"}}/></div>
                  <div><FieldLabel>Special Tests (if any)</FieldLabel><textarea rows={3} value={selected.specialTests||""} onChange={(e)=>setSelected((s)=>({...s,specialTests:e.target.value}))} placeholder="SLR, McMurray, Lachman, Neer, Hawkins-Kennedy, etc." style={{resize:"vertical"}}/></div>
                  <div><FieldLabel>Attach Diagnostic File (PDF / Image)</FieldLabel><input type="file" accept=".pdf,image/*" onChange={(e)=>setAttachmentFile(e.target.files?.[0]||null)}/>{attachmentFile&&<div style={{marginTop:8,fontSize:12,color:T.textMid}}>Selected: {attachmentFile.name}</div>}</div>
                  <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
                    <button className="btn btn-gold" onClick={saveDiagnosticDetails} disabled={savingDiagnostics}>{savingDiagnostics?"Saving...":"Save Diagnostic Details"}</button>
                    <button className="btn btn-outline" onClick={uploadPatientAttachment} disabled={uploadingAttachment}>{uploadingAttachment?"Uploading...":"Upload Attachment"}</button>
                  </div>
                  <div style={{background:T.panel,borderRadius:10,padding:18,border:`1px solid ${T.border}`}}>
                    <div style={{fontSize:11,color:T.textDim,textTransform:"uppercase",letterSpacing:"0.6px",marginBottom:10}}>Uploaded Attachments</div>
                    {loadingAttachments?<div style={{fontSize:13,color:T.textDim}}>Loading attachments...</div>:patientAttachments.length===0?<div style={{fontSize:13,color:T.textDim}}>No attachments uploaded yet.</div>:(
                      <div style={{display:"grid",gap:10}}>
                        {patientAttachments.map((file)=>(
                          <div key={file.id} style={{background:"rgba(255,255,255,0.02)",border:`1px solid ${T.border}`,borderRadius:8,padding:"10px 12px",display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
                            <div style={{minWidth:0,flex:1}}>
                              <div style={{fontSize:13,color:T.text,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{file.file_name}</div>
                              <div style={{fontSize:11,color:T.textDim}}>{file.file_type||"file"}</div>
                            </div>
                            <div style={{display:"flex",gap:8}}>
                              <button className="btn btn-outline btn-sm" onClick={async()=>{const{data,error}=await supabase.storage.from("patient-attachments").createSignedUrl(file.file_url,300);if(error){notify("Opening file failed: "+error.message,"error");return;}if(!data?.signedUrl){notify("No file URL could be generated","error");return;}window.open(data.signedUrl,"_blank","noopener,noreferrer");}}>Open</button>
                              <button className="btn btn-danger btn-sm" onClick={()=>deletePatientAttachment(file)}>Delete</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {detailTab==="histories"&&(
              <div className="card" style={{padding:28}}>
                <SectionLabel text="Patient Histories"/>
                <div style={{display:"grid",gridTemplateColumns:"1fr",gap:8}}>
                  <div><FieldLabel>Past History</FieldLabel><textarea rows={3} value={selected.pastHistory||""} onChange={(e)=>setSelected((s)=>({...s,pastHistory:e.target.value}))} placeholder="Past history details..." style={{resize:"vertical"}}/></div>
                  <div><FieldLabel>Medical History</FieldLabel><textarea rows={3} value={selected.medicalHistory||""} onChange={(e)=>setSelected((s)=>({...s,medicalHistory:e.target.value}))} placeholder="Medical history details..." style={{resize:"vertical"}}/></div>
                  <div><FieldLabel>Surgical History</FieldLabel><textarea rows={3} value={selected.surgicalHistory||""} onChange={(e)=>setSelected((s)=>({...s,surgicalHistory:e.target.value}))} placeholder="Surgical history details..." style={{resize:"vertical"}}/></div>
                  <div style={{display:"flex",gap:12}}><button className="btn btn-gold" onClick={savePatientHistories} disabled={savingDiagnostics}>{savingDiagnostics?"Saving...":"Save Histories"}</button></div>
                </div>
              </div>
            )}

            {detailTab==="home exercise programme"&&(
              <div className="card" style={{padding:28}}>
                <SectionLabel text="Home Exercise Programme"/>
                {homeProgramme.programme_title&&!editingHomeProgramme?(
                  <div>
                    <div style={{background:T.panel,border:`1px solid ${T.border}`,borderRadius:10,padding:20,marginBottom:20}}>
                      <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:24,fontWeight:700,color:T.goldLight,marginBottom:8}}>{homeProgramme.programme_title}</div>
                      <div style={{fontSize:13,color:T.textDim,marginBottom:14}}>{homeProgramme.condition}</div>
                      <div style={{marginBottom:14}}><FieldLabel>Goal</FieldLabel><div style={{color:T.textMid,lineHeight:1.8}}>{homeProgramme.goal||"Not recorded"}</div></div>
                      <div style={{marginBottom:14}}><FieldLabel>Exercise List</FieldLabel><div style={{color:T.textMid,lineHeight:1.8,whiteSpace:"pre-wrap"}}>{homeProgramme.exercise_list||"Not recorded"}</div></div>
                      <div style={{marginBottom:14}}><FieldLabel>Precautions</FieldLabel><div style={{color:T.textMid,lineHeight:1.8,whiteSpace:"pre-wrap"}}>{homeProgramme.precautions||"Not recorded"}</div></div>
                      <div style={{marginBottom:14}}><FieldLabel>Patient Advice</FieldLabel><div style={{color:T.textMid,lineHeight:1.8,whiteSpace:"pre-wrap"}}>{homeProgramme.patient_advice||"Not recorded"}</div></div>
                      <div style={{marginBottom:18}}><FieldLabel>Video Link</FieldLabel><div style={{color:T.textMid,lineHeight:1.8}}>{homeProgramme.video_link?(<a href={homeProgramme.video_link} target="_blank" rel="noreferrer" style={{color:T.gold}}>Open Video Tutorial</a>):"Not added"}</div></div>
                      <div style={{display:"flex",gap:12,flexWrap:"wrap"}}>
                        <button className="btn btn-gold" onClick={()=>setEditingHomeProgramme(true)}>Edit Programme</button>
                        <button className="btn btn-outline" onClick={()=>{
                          const message=formatHomeProgrammeForWhatsApp(selected,homeProgramme,clinicSettings.clinic_name);
                          const phone=(selected.phone||"").replace(/\D/g,"");
                          if(!phone){notify("Patient phone number is missing","error");return;}
                          window.open(`https://wa.me/${phone}?text=${message}`,"_blank");
                        }}>Send to Patient</button>
                        <button className="btn btn-outline" onClick={()=>{
                          const html=`<html><head><title>Home Exercise Programme</title><style>body{font-family:Arial,sans-serif;padding:40px;color:#111;line-height:1.7;}h1,h2{margin-bottom:8px;}.section{margin-bottom:24px;}.label{font-weight:bold;margin-bottom:6px;}.box{padding:12px;border:1px solid #ccc;border-radius:8px;white-space:pre-wrap;}</style></head><body><h1>${clinicSettings.clinic_name||"Clinic"}</h1><p><strong>Patient:</strong> ${selected.name}</p><p><strong>Patient ID:</strong> ${selected.patientId||"Not assigned"}</p><div class="section"><div class="label">Programme Title</div><div class="box">${homeProgramme.programme_title||""}</div></div><div class="section"><div class="label">Condition</div><div class="box">${homeProgramme.condition||""}</div></div><div class="section"><div class="label">Goal</div><div class="box">${homeProgramme.goal||""}</div></div><div class="section"><div class="label">Exercise List</div><div class="box">${homeProgramme.exercise_list||""}</div></div><div class="section"><div class="label">Precautions</div><div class="box">${homeProgramme.precautions||""}</div></div><div class="section"><div class="label">Patient Advice</div><div class="box">${homeProgramme.patient_advice||""}</div></div><div class="section"><div class="label">Video Link</div><div class="box">${homeProgramme.video_link||"Not provided"}</div></div><script>window.onload=function(){window.print();};</script></body></html>`;
                          const win=window.open("","_blank");
                          win.document.write(html);
                          win.document.close();
                        }}>Print / PDF</button>
                        <button className="btn btn-danger" onClick={deleteHomeProgramme}>Delete Programme</button>
                      </div>
                    </div>
                  </div>
                ):(
                  <div style={{display:"grid",gridTemplateColumns:"1fr",gap:16}}>
                    <div><FieldLabel>Programme Title</FieldLabel><input value={homeProgramme.programme_title} onChange={(e)=>setHomeProgramme((p)=>({...p,programme_title:e.target.value}))} placeholder="e.g. Home Programme — Tennis Elbow"/></div>
                    <div><FieldLabel>Condition</FieldLabel><input value={homeProgramme.condition} onChange={(e)=>setHomeProgramme((p)=>({...p,condition:e.target.value}))} placeholder="Condition name"/></div>
                    <div><FieldLabel>Goal</FieldLabel><textarea rows={3} value={homeProgramme.goal} onChange={(e)=>setHomeProgramme((p)=>({...p,goal:e.target.value}))} placeholder="What is the goal of this home programme?" style={{resize:"vertical"}}/></div>
                    <div><FieldLabel>Exercise List</FieldLabel><textarea rows={6} value={homeProgramme.exercise_list} onChange={(e)=>setHomeProgramme((p)=>({...p,exercise_list:e.target.value}))} placeholder="Enter exercises with sets / reps / hold times..." style={{resize:"vertical"}}/></div>
                    <div><FieldLabel>Precautions</FieldLabel><textarea rows={3} value={homeProgramme.precautions} onChange={(e)=>setHomeProgramme((p)=>({...p,precautions:e.target.value}))} placeholder="Any precautions or warning signs..." style={{resize:"vertical"}}/></div>
                    <div><FieldLabel>Patient Advice</FieldLabel><textarea rows={4} value={homeProgramme.patient_advice} onChange={(e)=>setHomeProgramme((p)=>({...p,patient_advice:e.target.value}))} placeholder="Extra instructions for the patient..." style={{resize:"vertical"}}/></div>
                    <div><FieldLabel>Video Link (optional)</FieldLabel><input value={homeProgramme.video_link} onChange={(e)=>setHomeProgramme((p)=>({...p,video_link:e.target.value}))} placeholder="Paste YouTube / Drive / tutorial link"/></div>
                    <div style={{display:"flex",gap:12,marginTop:12}}>
                      <button className="btn btn-gold" onClick={saveHomeProgramme} disabled={savingHomeProgramme}>{savingHomeProgramme?"Saving...":"Save Home Exercise Programme"}</button>
                      {homeProgramme.programme_title&&(<button className="btn btn-outline" onClick={()=>setEditingHomeProgramme(false)}>Cancel</button>)}
                    </div>
                  </div>
                )}
              </div>
            )}
 
            {detailTab==="billing"&&selected&&session&&(
  <BillingTab
    patient={selected}
    session={session}
    clinicSettings={clinicSettings}
    notify={notify}
  />
)}
            {detailTab==="outcomes"&&selected&&session&&(
              <div style={{padding:"0 0 30px 0"}}>
                <OutcomeMeasures patient={selected} session={session} notify={notify}/>
              </div>
            )}

            {detailTab==="soap ai"&&selected&&session&&(
              <div style={{padding:"0 0 30px 0"}}>
                <SOAPNoteAI key={selected.id} patient={selected} session={session} notify={notify} effectiveJoint={getEffectiveJoint(selected)}/>
              </div>
            )}

            {detailTab==="progress"&&selected&&session&&(
              <ProgressAnalytics key={selected.id} patient={selected} session={session} notify={notify} effectiveJoint={getEffectiveJoint(selected)}/>
            )}
          </div>
        )}

        {page==="schedule"&&(
          <div style={{padding:"30px 16px"}} className="fade-up">
            <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:28,fontWeight:600,marginBottom:4}}>Appointment Schedule</div>
            <button
              onClick={()=>{
                const now=new Date();
                const offset=now.getTimezoneOffset();
                const localDate=new Date(now.getTime()-(offset*60*1000));
                const todayStr=localDate.toISOString().split("T")[0];
                const todaysPatients=patients.filter(p=>p.nextVisit===todayStr&&p.status==="Active");
                if(todaysPatients.length===0){alert(`No appointments found for today (${todayStr}).`);return;}
                todaysPatients.forEach((p,index)=>{
                  const message=`Hi ${p.name}, this is a reminder for your appointment at ${clinicSettings.clinic_name||"our clinic"} today. See you soon!`;
                  const cleanNumber=(p.phone||"").replace(/\D/g,"");
                  const url=`https://wa.me/${cleanNumber}?text=${encodeURIComponent(message)}`;
                  setTimeout(()=>{window.open(url,"_blank");},index*1000);
                });
              }}
              style={{backgroundColor:"#25D366",color:"white",padding:"10px 20px",borderRadius:"8px",border:"none",fontWeight:"bold",cursor:"pointer",marginTop:"10px",marginBottom:"20px"}}
            >
              🚀 Send All Today's Reminders
            </button>
            <div style={{color:T.textMid,fontSize:13,marginBottom:26}}>Upcoming visits ordered by date</div>
            {patients.filter((p)=>p.nextVisit&&p.status==="Active"&&p.nextVisit>=new Date().toISOString().split("T")[0]).length===0?(
              <div className="card" style={{padding:60,textAlign:"center",color:T.textDim,fontSize:14}}>No upcoming appointments. Edit patient records to add visit dates.</div>
            ):(
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:8}}>
                {patients.filter((p)=>p.nextVisit&&p.status==="Active"&&p.nextVisit>=new Date().toISOString().split("T")[0]).sort((a,b)=>(a.nextVisit>b.nextVisit?1:-1)).map((p)=>(
                  <div key={p.id} className="card row-hover" style={{padding:22,cursor:"pointer",borderTop:`2px solid ${p.nextVisit===TODAY?T.gold:T.border}`}} onClick={()=>{setSelected(p);setPage("patients");setDetailTab("overview");fetchPatientAttachments(p.id);}}>
                    {p.nextVisit===TODAY&&<div style={{fontSize:9,fontWeight:700,color:T.gold,letterSpacing:"1.2px",textTransform:"uppercase",marginBottom:10}}>● Today</div>}
                    <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:14}}>
                      <Avatar name={p.name} size={42}/>
                      <div><div style={{fontWeight:600,fontSize:14}}>{p.name}</div><div style={{fontSize:11,color:T.textMid,lineHeight:1.3}}>{p.condition}</div></div>
                    </div>
                    <Divider my={12}/>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                      {[["Date",p.nextVisit],["Session #",p.sessions+1],["Therapist",p.therapist],["NPRS",`${p.painScore}/10 · ${getPainLabel(p.painScore)}`]].map(([label,value])=>(
                        <div key={label}><div style={{fontSize:9,color:T.textDim,marginBottom:2,textTransform:"uppercase",letterSpacing:"0.6px"}}>{label}</div><div style={{fontSize:11,fontWeight:500,color:label==="NPRS"?getPainColor(p.painScore):T.textMid}}>{value}</div></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {page==="analytics"&&(
          <Analytics key="analytics-page" patients={patients} therapists={therapists} stats={stats}/>
        )}

        {page==="knowledge"&&(
          <KnowledgeHub/>
        )}

        {page==="research"&&hasFeature("research")&&(
          <ResearchModule session={session} patients={patients} notify={notify}/>
        )}

        {page==="research"&&!hasFeature("research")&&(
          <div style={{padding:"30px 16px"}} className="fade-up">
            <div className="card" style={{padding:36,textAlign:"center",maxWidth:700,margin:"40px auto"}}>
              <div style={{fontSize:42,marginBottom:14}}>🔒</div>
              <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:28,fontWeight:700,color:T.goldLight,marginBottom:8}}>Research Module is a Plus Feature</div>
              <div style={{fontSize:14,color:T.textMid,lineHeight:1.8,marginBottom:22}}>Upgrade to Plus to create clinical studies, collect structured patient data, and export datasets for advanced analysis.</div>
              <div style={{display:"flex",justifyContent:"center",gap:12,flexWrap:"wrap"}}>
                <button className="btn btn-gold" onClick={()=>openUpgrade("Upgrade to Plus to unlock the Research Module.","research")}>⚡ Upgrade to Plus</button>
                <button className="btn btn-outline" onClick={()=>navigate("dashboard")}>Back to Dashboard</button>
              </div>
            </div>
          </div>
        )}

        {page==="settings"&&(
          <div style={{padding:"30px 16px"}} className="fade-up">
            <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:28,fontWeight:600,marginBottom:4}}>Clinic Settings</div>
            <div style={{color:T.textMid,fontSize:13,marginBottom:26}}>Manage your clinic profile. These settings are unique for each logged-in user.</div>
            {!editingClinicSettings?(
              <div className="card" style={{padding:28,maxWidth:760}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:20}}>
                  {[["Clinic Name",clinicSettings.clinic_name],["Clinical Incharge / Admin",clinicSettings.owner_name],["Clinic Phone",clinicSettings.phone],["Clinic Email",clinicSettings.email],["Signature Name",clinicSettings.signature_name],["Designation",clinicSettings.designation]].map(([label,value])=>(
                    <div key={label} style={{background:T.panel,borderRadius:10,padding:16,border:`1px solid ${T.border}`}}>
                      <div style={{fontSize:10,color:T.textDim,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.7px"}}>{label}</div>
                      <div style={{fontSize:14,color:T.text,fontWeight:600}}>{value||"Not set"}</div>
                    </div>
                  ))}
                  <div style={{gridColumn:"1 / -1",background:T.panel,borderRadius:10,padding:16,border:`1px solid ${T.border}`}}>
                    <div style={{fontSize:10,color:T.textDim,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.7px"}}>Clinic Address</div>
                    <div style={{fontSize:14,color:T.text,fontWeight:600,lineHeight:1.7}}>{clinicSettings.address||"Not set"}</div>
                  </div>
                  <div style={{gridColumn:"1 / -1",background:T.panel,borderRadius:10,padding:16,border:`1px solid ${T.border}`,marginTop:8}}>
                    <div style={{fontSize:10,color:T.textDim,marginBottom:6,textTransform:"uppercase",letterSpacing:"0.7px"}}>Signature Image</div>
                    <div style={{fontSize:14,color:T.text,fontWeight:600,lineHeight:1.7,wordBreak:"break-word"}}>{clinicSettings.signature_image_url||"Not set"}</div>
                  </div>
                </div>
                <button className="btn btn-gold" onClick={()=>setEditingClinicSettings(true)}>Edit Settings</button>
              </div>
            ):(
              <div className="card" style={{padding:28,maxWidth:760}}>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:20}}>
                  <div><FieldLabel>Clinic Name</FieldLabel><input value={clinicSettings.clinic_name} onChange={(e)=>setClinicSettings((s)=>({...s,clinic_name:e.target.value}))} placeholder="Enter clinic name"/></div>
                  <div><FieldLabel>Clinical Incharge / Admin Name</FieldLabel><input value={clinicSettings.owner_name} onChange={(e)=>setClinicSettings((s)=>({...s,owner_name:e.target.value}))} placeholder="Enter clinical incharge name"/></div>
                  <div><FieldLabel>Clinic Phone</FieldLabel><input value={clinicSettings.phone} onChange={(e)=>setClinicSettings((s)=>({...s,phone:e.target.value}))} placeholder="+91 XXXXX XXXXX"/></div>
                  <div><FieldLabel>Clinic Email</FieldLabel><input type="email" value={clinicSettings.email} onChange={(e)=>setClinicSettings((s)=>({...s,email:e.target.value}))} placeholder="clinic@example.com"/></div>
                  <div style={{gridColumn:"1 / -1"}}><FieldLabel>Clinic Address</FieldLabel><textarea rows={4} value={clinicSettings.address} onChange={(e)=>setClinicSettings((s)=>({...s,address:e.target.value}))} placeholder="Enter clinic address" style={{resize:"vertical"}}/></div>
                  <div><FieldLabel>Signature Name</FieldLabel><input value={clinicSettings.signature_name} onChange={(e)=>setClinicSettings((s)=>({...s,signature_name:e.target.value}))} placeholder="Enter signature name"/></div>
                  <div><FieldLabel>Designation</FieldLabel><input value={clinicSettings.designation} onChange={(e)=>setClinicSettings((s)=>({...s,designation:e.target.value}))} placeholder="Enter designation"/></div>
                  <div style={{gridColumn:"1 / -1"}}><FieldLabel>Signature Image URL</FieldLabel><input value={clinicSettings.signature_image_url} onChange={(e)=>setClinicSettings((s)=>({...s,signature_image_url:e.target.value}))} placeholder="Paste uploaded signature image URL"/></div>
                  <div style={{gridColumn:"1 / -1"}}>
                    <FieldLabel>Upload Signature Image</FieldLabel>
                    <input type="file" accept="image/*" onChange={(e)=>setSignatureFile(e.target.files?.[0]||null)}/>
                    {signatureFile&&<div style={{marginTop:8,fontSize:12,color:T.textMid}}>Selected: {signatureFile.name}</div>}
                    <div style={{display:"flex",gap:12,marginTop:12,flexWrap:"wrap"}}>
                      <button className="btn btn-outline" onClick={uploadSignatureImage} disabled={uploadingSignature}>{uploadingSignature?"Uploading...":"Upload Signature"}</button>
                    </div>
                  </div>
                  {clinicSettings.signature_image_url&&(
                    <div style={{gridColumn:"1 / -1",marginTop:16,background:T.panel,border:`1px solid ${T.border}`,borderRadius:10,padding:16}}>
                      <div style={{fontSize:10,color:T.textDim,marginBottom:10,textTransform:"uppercase",letterSpacing:"0.7px"}}>Signature Preview</div>
                      <button className="btn btn-outline btn-sm" onClick={async()=>{const{data,error}=await supabase.storage.from("user-signatures").createSignedUrl(clinicSettings.signature_image_url,300);if(error){notify("Could not load signature preview: "+error.message,"error");return;}window.open(data.signedUrl,"_blank");}}>Open Signature Preview</button>
                    </div>
                  )}
                </div>
                <div style={{display:"flex",gap:12}}>
                  <button className="btn btn-gold" onClick={async()=>{await saveClinicSettings();setEditingClinicSettings(false);}} disabled={savingClinicSettings}>{savingClinicSettings?"Saving...":"Save Settings"}</button>
                  <button className="btn btn-outline" onClick={()=>setEditingClinicSettings(false)}>Cancel</button>
                </div>
              </div>
            )}

            <div className="card" style={{padding:28,maxWidth:760,marginTop:20}}>
              <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:24,fontWeight:700,color:T.goldLight,marginBottom:8}}>Change Password</div>
              <div style={{color:T.textMid,fontSize:13,marginBottom:20}}>Use a secure password to protect your clinic account.</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:20}}>
                <div><FieldLabel>New Password</FieldLabel><input type="password" value={newPassword} onChange={(e)=>setNewPassword(e.target.value)} placeholder="Min 8 characters"/></div>
                <div><FieldLabel>Confirm New Password</FieldLabel><input type="password" value={confirmNewPassword} onChange={(e)=>setConfirmNewPassword(e.target.value)} placeholder="Confirm new password"/></div>
              </div>
              {passwordMessage&&(
                <div style={{background:"rgba(34,197,94,0.08)",border:"1px solid rgba(34,197,94,0.25)",borderRadius:8,padding:"10px 14px",fontSize:13,color:"#86efac",marginBottom:16}}>{passwordMessage}</div>
              )}
              <button className="btn btn-gold" onClick={handleChangePassword} disabled={changingPassword}>{changingPassword?"Updating...":"Update Password"}</button>
            </div>

            <div className="card" style={{padding:28,maxWidth:760,marginTop:20}}>
              <TeamManagement
                currentOrg={currentOrg}
                teamMembers={teamMembers}
                loading={orgLoading}
                inviteTeamMember={inviteTeamMember}
                removeTeamMember={removeTeamMember}
                therapistLimit={therapistLimit}
                isTrialActive={isTrialActive}
                notify={notify}
              />
            </div>
          </div>
        )}
      </div>

      {/* PATIENT FORM MODAL */}
      {showPatientForm&&(
        <div className="modal-backdrop" onClick={(e)=>{if(e.target===e.currentTarget)setShowPatientForm(false);}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,width:"100%",maxWidth:680,maxHeight:"92vh",overflowY:"auto",boxShadow:"0 30px 80px rgba(0,0,0,0.7)"}}>
            <div style={{padding:"22px 28px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",position:"sticky",top:0,background:T.card,zIndex:10}}>
              <div>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:22,fontWeight:600,color:T.goldLight}}>{editPatientId?"Edit Patient Record":"Register New Patient"}</div>
                <div style={{fontSize:11,color:T.textDim,marginTop:2}}>Saved directly to Supabase database</div>
              </div>
              <button onClick={()=>setShowPatientForm(false)} style={{background:"none",border:"none",color:T.textMid,fontSize:22,cursor:"pointer",padding:4}}>✕</button>
            </div>
            <div style={{padding:28}}>
              <div style={{fontSize:12,color:T.textDim,marginBottom:18}}>Only fields marked * are required.</div>
              <SectionLabel text="Personal Information"/>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:13,marginBottom:22}}>
                <div><FieldLabel>Patient ID</FieldLabel><input value={patientForm.patientId} onChange={(e)=>updatePatientForm("patientId",e.target.value)} placeholder="Enter custom patient ID"/></div>
                <div><FieldLabel>Patient Name *</FieldLabel><input value={patientForm.name} onChange={(e)=>updatePatientForm("name",e.target.value)} placeholder="Full name e.g. Mr. Swaminathan"/></div>
                <div><FieldLabel>Age *</FieldLabel><input type="number" value={patientForm.age} onChange={(e)=>updatePatientForm("age",e.target.value)} placeholder="Age"/></div>
                <div><FieldLabel>Gender *</FieldLabel><select value={patientForm.gender} onChange={(e)=>updatePatientForm("gender",e.target.value)}><option>Male</option><option>Female</option><option>Other</option></select></div>
                <div><FieldLabel>Blood Group</FieldLabel><select value={patientForm.blood} onChange={(e)=>updatePatientForm("blood",e.target.value)}>{BLOOD_GROUPS.map((b)=><option key={b}>{b}</option>)}</select></div>
                <div><FieldLabel>Phone *</FieldLabel><input value={patientForm.phone} onChange={(e)=>updatePatientForm("phone",e.target.value)} placeholder="+91 XXXXX XXXXX"/></div>
                <div><FieldLabel>Email</FieldLabel><input type="email" value={patientForm.email} onChange={(e)=>updatePatientForm("email",e.target.value)} placeholder="email@example.com"/></div>
                <div><FieldLabel>Join Date</FieldLabel><input type="date" value={patientForm.joinDate} onChange={(e)=>updatePatientForm("joinDate",e.target.value)}/></div>
                <div style={{gridColumn:"1 / -1"}}><FieldLabel>Address</FieldLabel><input value={patientForm.address} onChange={(e)=>updatePatientForm("address",e.target.value)} placeholder="Full address"/></div>
              </div>
              <Divider my={18}/>
              <SectionLabel text="Clinical Details"/>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:13,marginBottom:22}}>
                <div style={{gridColumn:"1 / -1"}}>
                  <FieldLabel>Diagnosis / Condition *</FieldLabel>
                  <select value={patientForm.condition} onChange={(e)=>{
                    const newCondition=e.target.value;
                    const autoJoint=inferJointFromCondition(newCondition,patientForm.customCondition);
                    setPatientForm((f)=>({...f,condition:newCondition,jointSelection:newCondition==="Other"?f.jointSelection:autoJoint||f.jointSelection}));
                  }}>
                    <option value="">Select condition…</option>
                    {CONDITIONS.map((c)=><option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                {patientForm.condition==="Other"&&(
                  <div style={{gridColumn:"1 / -1"}}>
                    <FieldLabel>Enter Actual Diagnosis *</FieldLabel>
                    <input value={patientForm.customCondition||""} onChange={(e)=>{
                      const newCustom=e.target.value;
                      const autoJoint=inferJointFromCondition("Other",newCustom);
                      setPatientForm((f)=>({...f,customCondition:newCustom,jointSelection:autoJoint||f.jointSelection}));
                    }} placeholder="Type the actual diagnosis"/>
                  </div>
                )}
                <div>
                  <FieldLabel>Joint / Region Selection</FieldLabel>
                  <select value={patientForm.jointSelection} onChange={(e)=>updatePatientForm("jointSelection",e.target.value)}>
                    <option value="">Select joint / region…</option>
                    <option value="shoulder">Shoulder</option>
                    <option value="elbow">Elbow</option>
                    <option value="wrist_hand">Wrist / Hand</option>
                    <option value="cervical_spine">Cervical Spine</option>
                    <option value="thoracic_spine">Thoracic Spine</option>
                    <option value="lumbar_spine">Lumbar Spine</option>
                    <option value="hip">Hip</option>
                    <option value="knee">Knee</option>
                    <option value="ankle_foot">Ankle / Foot</option>
                    <option value="neuro">Neuro</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <FieldLabel>NPRS Score (0–10) *</FieldLabel>
                  <select value={patientForm.painScore} onChange={(e)=>updatePatientForm("painScore",Number(e.target.value))}>
                    {[0,1,2,3,4,5,6,7,8,9,10].map((score)=><option key={score} value={score}>{score}</option>)}
                  </select>
                </div>
                <div>
                  <FieldLabel>Assigned Therapist *</FieldLabel>
                  <select value={patientForm.therapist} onChange={(e)=>updatePatientForm("therapist",e.target.value)}>
                    <option value="">Select therapist…</option>
                    {therapists.map((t)=><option key={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div><FieldLabel>Sessions Prescribed</FieldLabel><input type="number" value={patientForm.totalSessions} onChange={(e)=>updatePatientForm("totalSessions",Number(e.target.value))} placeholder="12"/></div>
                <div><FieldLabel>Next Visit Date *</FieldLabel><input type="date" value={patientForm.nextVisit} onChange={(e)=>updatePatientForm("nextVisit",e.target.value)}/></div>
                <div><FieldLabel>Status</FieldLabel><select value={patientForm.status} onChange={(e)=>updatePatientForm("status",e.target.value)}><option>Active</option><option>Completed</option></select></div>
              </div>
              <Divider my={18}/>
              <SectionLabel text="Vitals"/>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:13,marginBottom:22}}>
                <div><FieldLabel>Blood Pressure</FieldLabel><input value={patientForm.bp||""} onChange={(e)=>updatePatientForm("bp",e.target.value)} placeholder="120/80"/></div>
                <div><FieldLabel>Pulse (bpm)</FieldLabel><input type="number" value={patientForm.pulse||""} onChange={(e)=>updatePatientForm("pulse",e.target.value)} placeholder="72"/></div>
              </div>
              <Divider my={18}/>
              <SectionLabel text="Billing"/>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:13,marginBottom:22}}>
                <div><FieldLabel>Amount Billed (₹)</FieldLabel><input type="number" value={patientForm.amount} onChange={(e)=>updatePatientForm("amount",Number(e.target.value))} placeholder="0"/></div>
                <div><FieldLabel>Payment Status</FieldLabel><select value={patientForm.payments} onChange={(e)=>updatePatientForm("payments",e.target.value)}><option>Pending</option><option>Paid</option><option>Partial</option></select></div>
              </div>
              <Divider my={18}/>
              <SectionLabel text="Diagnostic Details"/>
              <div style={{marginBottom:16}}><FieldLabel>Diagnostic Tools</FieldLabel><textarea rows={3} value={patientForm.diagnosticTools} onChange={(e)=>updatePatientForm("diagnosticTools",e.target.value)} placeholder="MRI, X-ray, CT, lab reports, ultrasound, etc." style={{resize:"vertical"}}/></div>
              <div style={{marginBottom:16}}><FieldLabel>Outcome Measures</FieldLabel><textarea rows={3} value={patientForm.outcomeMeasures} onChange={(e)=>updatePatientForm("outcomeMeasures",e.target.value)} placeholder="NPRS, ROM, Oswestry, DASH, WOMAC, etc." style={{resize:"vertical"}}/></div>
              <div style={{marginBottom:24}}><FieldLabel>Notes</FieldLabel><textarea rows={4} value={patientForm.notes} onChange={(e)=>updatePatientForm("notes",e.target.value)} placeholder="Additional clinical notes, precautions, observations, home advice..." style={{resize:"vertical"}}/></div>
              <div style={{display:"flex",gap:12}}>
                <button className="btn btn-gold" style={{flex:1,padding:"12px"}} onClick={handleSavePatient} disabled={savingPatient}>{savingPatient?"Saving…":editPatientId?"Save Changes":"Register Patient"}</button>
                <button className="btn btn-outline" onClick={()=>setShowPatientForm(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* THERAPIST FORM MODAL */}
      {showTherapistForm&&(
        <div className="modal-backdrop" onClick={(e)=>{if(e.target===e.currentTarget)setShowTherapistForm(false);}}>
          <div style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:14,width:"100%",maxWidth:480,boxShadow:"0 30px 80px rgba(0,0,0,0.7)"}}>
            <div style={{padding:"22px 28px",borderBottom:`1px solid ${T.border}`,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div>
                <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:22,fontWeight:600,color:T.goldLight}}>{editTherapistId?"Edit Therapist":"Add New Therapist"}</div>
                <div style={{fontSize:11,color:T.textDim,marginTop:2}}>Available immediately in patient forms after saving</div>
              </div>
              <button onClick={()=>setShowTherapistForm(false)} style={{background:"none",border:"none",color:T.textMid,fontSize:22,cursor:"pointer",padding:4}}>✕</button>
            </div>
            <div style={{padding:28}}>
              <div style={{marginBottom:16}}><FieldLabel>Full Name * (e.g. Dr. Ananya Krishnan)</FieldLabel><input value={therapistForm.name} onChange={(e)=>updateTherapistForm("name",e.target.value)} placeholder="Dr. Full Name"/></div>
              <div style={{marginBottom:16}}><FieldLabel>Specialty</FieldLabel><input value={therapistForm.specialty} onChange={(e)=>updateTherapistForm("specialty",e.target.value)} placeholder="e.g. Musculoskeletal & Sports, Neuro Rehab, Paediatrics…"/></div>
              <div style={{marginBottom:24}}>
                <FieldLabel>Avatar Initials (optional — auto-generated if blank)</FieldLabel>
                <input value={therapistForm.avatar} onChange={(e)=>updateTherapistForm("avatar",e.target.value.toUpperCase().slice(0,3))} placeholder="e.g. AK — leave blank to auto-generate" maxLength={3}/>
                <div style={{fontSize:11,color:T.textDim,marginTop:8,display:"flex",alignItems:"center",gap:10}}>
                  Preview:
                  <div style={{width:38,height:38,borderRadius:"50%",background:getAvatarColor(therapistForm.name||"X"),display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:"#fff"}}>{therapistForm.avatar||autoAvatar(therapistForm.name)||"?"}</div>
                  <span style={{color:T.textMid}}>{therapistForm.name||"Enter name above"}</span>
                </div>
              </div>
              <div style={{display:"flex",gap:12}}>
                <button className="btn btn-gold" style={{flex:1,padding:"12px"}} onClick={handleSaveTherapist} disabled={savingTherapist}>{savingTherapist?"Saving…":editTherapistId?"Save Changes":"Add Therapist"}</button>
                <button className="btn btn-outline" onClick={()=>setShowTherapistForm(false)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}